// Project based config
// 	The only other differences between projects are in:
//		SetupLoader.jsx
//		MapLoader.jsx & dependant files

import {
	changeHeaderTransform,
	changeTransform,
	confidenceTransform,
	confidenceHeaderTransform,
	addLineHeightTransform,
	sortChange,
	sortConfidence,
} from "lib/table/tableTransforms.js";

import {
	baseContentColumns,
	contentColumnsGECOnly,
	contentColumnsAus22Only,
	dateTypeTitleSharesContentColumns,
	// noDuplicatesContentColumns,
	noTypeContentColumns,
	// noTypeNoInteractionsContentColumns,
	noTypeNoDuplicatesContentColumns,
	noSharesContentColumns,
	vzlaContentColumns,
} from "lib/table/contentColumns.js";
import { contentTabBase, contentTabPushPullCountry, contentTabSource } from "lib/table/contentTabColumns.js";
import { us2020CrosstabsHeaders, gecCrosstabsHeaders, configureGECStats } from "lib/table/crosstabsColumns.js";
import { usFIPSLookup } from "lib/mapLookup.js";
import { predictSearchHeaders } from "lib/table/mediaColumns.js";
import { formatGECTags } from "lib/helpers/projectSpecificHelpers.js";
import { chinaUsStats, chinaUsStatsMongolia } from "lib/explodedDonutStatsConfig.js";

import { biasText, biasBullets, outletsText, outletsBullets, peopleText, peopleBullets } from "lib/helpers/crosstabsHelpers.js";

const projectConfig = {
	africom: {
		availableTabs: {
			default: "curated",
			asksage: false,
			curated: true,
			media: true, // need to set up api.dataKey
			reportBuilderLink: true,
			bigMoversLink: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			moe: false,
			narratives: false,
			social: false,
			video: false,
			web: false,
		},
		contentTabCsvHeaders: [
			{
				name: "Title",
				id: "title",
			},
			{
				name: "Source",
				id: "source",
			},
			{
				name: "Date",
				id: "postDate",
			},
		],
		contentTabTableHeaders: contentTabSource,
		contentTabMap: false,
		defaultZoom: "1 year",
		hideEntities: false,
		hideShare: true,
		defaultLightUI: false,
		userSearchHideLocation: true,
		mediaLanguages: ["English"],
		// timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270 },
		uiThemeToggle: true,
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
	},

	koala: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			crosstabs: false,
			narratives: true,
			bigMoversLink: false,
			curated: true,
			events: false,
			explorer: true,
			graph: false,
			media: true,
			moe: false,
			reportBuilderLink: false,
			social: true,
			video: true,
			web: true,
		},
		alwaysShowContentInteractions: true,
		// Default supports nested narratives with clickable main narrative
		compareHeaderConfig: false,
		contentColumns: contentColumnsAus22Only,
		defaultAggregationLevel: "weeklyTimeseries",
		// dmaMap: true,a
		topPhrasesAvailable: false,
		hideMap: false,
		entityFiltersAvailable: false,
		contentTabDateBegin: 1646114400000,
		mediaEntityFilters: true,
		defaultOriginalLanguage: true,
		mediaLanguages: ["All", "English", "Chinese"],
		mediaCountries: ["All", "Australia", "China"],
		googleTranslateLanguages: ["en", "zh-CN", "zh-TW"],
		exoveraEditButton: true,
		expandedContentShowTweets: true,
		fakeSingleArticle: false,
		graphDataTypes: [
			{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
			{ name: "Unique Article Count", id: "articleDedupCount", label: "Volume", perspectiveOverride: "Source" },
			{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
			// { name: "Article Reach", id: "articleReach", label: "Reach" },
		],
		hideEntities: false,
		hotArticleLimit: {
			hot: 1000,
			hotter: 5000,
			hottest: 10000,
		},
		configureStats: configureGECStats,
		defaultZoom: "1 year",
		hideParentNarrativeTags: false,
		highlightedEntitiesTitle: "Candidates",
		defaultLightUI: false,
		defaultSearchTranslationEnabled: true,
		defaultSearchTranslationLanguages: ["zh"],
		projectMapConfig: false,
		explorerDatasets: [
			{ name: "Procurement", id: "procurement", tableColumns: ["title"] },
			{
				name: "Entities",
				id: "rover",
				tableColumns: [
					"label_en",
					"label",
					// "aka",
					"entity_type",
					"categories",
					"primary_country",
					"sanctioned",
					// "sanction_authority",
					// "sanction_program",
				],
			},
			{ name: "Corporate Records", id: "corprecords", tableColumns: ["label", "business_scope", "industry", "enterprise_type"] },
			{ name: "Resumes", id: "resumes", tableColumns: ["name", "gender", "current_position", "age"] },
		],
		explorerShowEntities: ["procurement"],
		//   mediaLanguages: ["English", "Chinese"],
		// mapLookup: usFIPSLookup,
		socialAccounts: [
			{ name: "--", id: "all" },
			{ name: "Customer Accounts", id: "smToolCustomerList00" },
			{ name: "United Front Accounts", id: "smToolUnitedFront" },
			// { name: "Media Accounts", id: "smToolMedia" },
			{ name: "Chinese Media", id: "smToolChinaMedia" },
			{ name: "Australian Media", id: "smToolausMedia" },
			{ name: "PRC Government & Wolf Warriors", id: "smToolwolfWar" },
			{ name: "PLA", id: "smToolausPLA" },
			// { name: "Taliban", id: "aaaacccc-3333-4d2b-a01f-12378ddafa70" },
			// { name: "WeChat Only", id: "weChatOnly" },
			// { name: "Other Social", id: "otherSocial" },
		],
		socialPlatforms: [
			{ name: "--", id: "all" },
			{ name: "WeChat", id: "wechat" },
			{ name: "Weibo", id: "weibo" },
			{ name: "Twitter", id: "twitter" },
		],
		socialTopics: [
			{ name: "--", id: "all" },
			// { name: "All Topics", id: "allTracked" },
			{ name: "Australia Alliances", id: "alliancesTopic" },
			// { name: "Election", id: "electionTopic" },
			{ name: "Security", id: "securityTopic" },
			{ name: "Trade", id: "tradeTopic" },
			// { name: "Economics", id: "economicsTopic" },
			{ name: "Covid", id: "covidTopic" },
			{ name: "Discrimination", id: "racismTopic" },
			{ name: "Human Rights", id: "rightsTopic" },
			// { name: "Media", id: "mediaTopic" },
			// { name: "Immigration", id: "immigrationTopic" },
			{ name: "Australia in the Pacific", id: "pacificTopic" },
			// { name: "Environment", id: "pacificTopic" },
			{ name: "Australia in SE Asia", id: "seasiaTopic" },
			{ name: "PRC Official Travel", id: "prcofficialtravelTopic" },
			// { name: "Afghanistan", id: "d303b515-3333-4052-9603-889c5f823f10" },
			// { name: "RS/Nato", id: "e3adf36a-3333-41ca-8d84-ddc7d6cdc2cd" },
			// { name: "Other Regional", id: "b1976529-3333-44d4-82fd-4e4fc704808c" },
		],
		socialProvinces: [
			{ id: "all", name: "--" },
			{ id: "smToolcnshanxi", name: "Shanxi" },
			{ id: "smToolcninnermongolia", name: "Inner Mongolia " },
			{ id: "smToolcnliaoning", name: "Liaoning" },
			{ id: "smToolcnheilongjiang", name: "Heilongjiang" },
			{ id: "smToolcnjiangsu", name: "Jiangsu " },
			{ id: "smToolcnzhejiang", name: "Zhejiang" },
			{ id: "smToolcnanhui", name: "Anhui" },
			{ id: "smToolcnfujian", name: "Fujian" },
			{ id: "smToolcnjiangxi", name: "Jiangxi" },
			{ id: "smToolcnshandong", name: "Shandong" },
			{ id: "smToolcnhenan", name: "Henan" },
			{ id: "smToolcnhunan", name: "Hunan" },
			{ id: "smToolcnguangxi", name: "Guangxi" },
			{ id: "smToolcnhainan", name: "Hainan" },
			{ id: "smToolcnchongqing", name: "Chongqing" },
			{ id: "smToolcnsichuan", name: "Sichuan" },
			{ id: "smToolcnguizhou", name: "Guizhou" },
			{ id: "smToolcnyunnan", name: "Yunnan" },
			{ id: "smToolcntibet", name: "Tibet" },
			{ id: "smToolcnshaanxi", name: "Shaanxi" },
			{ id: "smToolcngansu", name: "Gansu" },
			{ id: "smToolcnqinghai", name: "Qinghai" },
			{ id: "smToolcnningxia", name: "Ningxia" },
			{ id: "smToolcnxinjiang", name: "Xinjiang" },
			{
				id: "smToolcnprovincial-levelpoliticalplatform",
				name: "Provincial-level Political Platform ",
			},
			{
				id: "smToolcnnational-centralnewsunit",
				name: "National - Central News Unit",
			},
			{ id: "smToolcnnational-expertmedia", name: "National - Expert Media " },
			{ id: "smToolcnnational-mediaplatforms", name: "National - Media Platforms" },
		],

		moeGraphAvailable: false,
		moeMapAvailable: false,
		overrideColors: true,
		showAllTopicsMap: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		// timezone: false,
		timezone: { name: " Australian Eastern Standard Time", offsetDisplay: "+10:00", offset: +0 },
		translateArticleTitles: false,
		uiThemeToggle: true,
		videoTabEntities: true,
		videoTabEntityGraph: true,
		videoTabStartDate: "01-01-2020", // "MM-DD-YYYY"
		videoTabTopics: false,
	},

	oseAusChina: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			crosstabs: false,
			narratives: true,
			bigMoversLink: false,
			curated: true,
			events: false,
			explorer: true,
			graph: false,
			media: true,
			moe: false,
			reportBuilderLink: false,
			social: true,
			video: true,
			web: true,
		},
		alwaysShowContentInteractions: true,
		// Default supports nested narratives with clickable main narrative
		compareHeaderConfig: false,
		contentColumns: contentColumnsAus22Only,
		defaultOriginalLanguage: true,
		defaultAggregationLevel: "weeklyTimeseries",
		defaultSearchTranslationEnabled: true,
		defaultSearchTranslationLanguages: ["zh"],
		// dmaMap: true,a
		topPhrasesAvailable: false,
		hideEntities: false,
		hideMap: false,
		// Leaving this here as an example but no longer needed since Australia map has been edded
		// hideMapFilterIds: [{ parent: "sourceCountry", id: "sourceCountry_China" }],
		entityFiltersAvailable: false,
		contentTabDateBegin: 1646114400000,
		mediaEntityFilters: false,
		mediaLanguages: ["All", "English", "Chinese"],
		mediaCountries: ["All", "Australia", "China"],
		googleTranslateLanguages: ["en", "zh-CN", "zh-TW"],
		exoveraEditButton: true,
		expandedContentShowTweets: true,
		fakeSingleArticle: false,
		graphDataTypes: [
			{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
			{ name: "Unique Article Count", id: "articleDedupCount", label: "Volume", perspectiveOverride: "Source" },
			{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
			// { name: "Article Reach", id: "articleReach", label: "Reach" },
		],
		hotArticleLimit: {
			hot: 1000,
			hotter: 5000,
			hottest: 10000,
		},
		configureStats: configureGECStats,
		defaultZoom: "1 year",
		hideParentNarrativeTags: false,
		highlightedEntitiesTitle: "Candidates",
		defaultLightUI: false,
		projectMapConfig: false,
		userSearchHideLocation: true,
		explorerDatasets: [
			{ name: "Procurement", id: "procurement", tableColumns: ["title"] },
			{
				name: "Entities",
				id: "rover",
				tableColumns: [
					"label_en",
					"label",
					// "aka",
					"entity_type",
					"categories",
					"primary_country",
					"sanctioned",
					// "sanction_authority",
					// "sanction_program",
				],
			},
			{ name: "Corporate Records", id: "corprecords", tableColumns: ["label", "business_scope", "industry", "enterprise_type"] },
			{ name: "Resumes", id: "resumes", tableColumns: ["name", "gender", "current_position", "age"] },
		],
		explorerShowEntities: ["procurement"],
		//   mediaLanguages: ["English", "Chinese"],
		// mapLookup: usFIPSLookup,
		socialAccounts: [
			{ name: "--", id: "all" },
			{ name: "Customer Accounts", id: "smToolCustomerList00" },
			{ name: "United Front Accounts", id: "smToolUnitedFront" },
			// { name: "Media Accounts", id: "smToolMedia" },
			{ name: "Chinese Media", id: "smToolChinaMedia" },
			{ name: "Australian Media", id: "smToolausMedia" },
			{ name: "PRC Government & Wolf Warriors", id: "smToolwolfWar" },
			{ name: "PLA", id: "smToolausPLA" },
			// { name: "Taliban", id: "aaaacccc-3333-4d2b-a01f-12378ddafa70" },
			// { name: "WeChat Only", id: "weChatOnly" },
			// { name: "Other Social", id: "otherSocial" },
		],
		socialPlatforms: [
			{ name: "--", id: "all" },
			{ name: "WeChat", id: "wechat" },
			{ name: "Weibo", id: "weibo" },
			{ name: "Twitter", id: "twitter" },
		],
		socialTopics: [
			{ name: "--", id: "all" },
			// { name: "All Topics", id: "allTracked" },
			{ name: "Australia Alliances", id: "alliancesTopic" },
			// { name: "Election", id: "electionTopic" },
			{ name: "Security", id: "securityTopic" },
			{ name: "Trade", id: "tradeTopic" },
			// { name: "Economics", id: "economicsTopic" },
			{ name: "Covid", id: "covidTopic" },
			{ name: "Discrimination", id: "racismTopic" },
			{ name: "Human Rights", id: "rightsTopic" },
			// { name: "Media", id: "mediaTopic" },
			// { name: "Immigration", id: "immigrationTopic" },
			{ name: "Australia in the Pacific", id: "pacificTopic" },
			// { name: "Environment", id: "pacificTopic" },
			{ name: "Australia in SE Asia", id: "seasiaTopic" },
			// { name: "Afghanistan", id: "d303b515-3333-4052-9603-889c5f823f10" },
			// { name: "RS/Nato", id: "e3adf36a-3333-41ca-8d84-ddc7d6cdc2cd" },
			// { name: "Other Regional", id: "b1976529-3333-44d4-82fd-4e4fc704808c" },
		],
		socialProvinces: [
			{ id: "all", name: "--" },
			{ id: "smToolcnshanxi", name: "Shanxi" },
			{ id: "smToolcninnermongolia", name: "Inner Mongolia " },
			{ id: "smToolcnliaoning", name: "Liaoning" },
			{ id: "smToolcnheilongjiang", name: "Heilongjiang" },
			{ id: "smToolcnjiangsu", name: "Jiangsu " },
			{ id: "smToolcnzhejiang", name: "Zhejiang" },
			{ id: "smToolcnanhui", name: "Anhui" },
			{ id: "smToolcnfujian", name: "Fujian" },
			{ id: "smToolcnjiangxi", name: "Jiangxi" },
			{ id: "smToolcnshandong", name: "Shandong" },
			{ id: "smToolcnhenan", name: "Henan" },
			{ id: "smToolcnhunan", name: "Hunan" },
			{ id: "smToolcnguangxi", name: "Guangxi" },
			{ id: "smToolcnhainan", name: "Hainan" },
			{ id: "smToolcnchongqing", name: "Chongqing" },
			{ id: "smToolcnsichuan", name: "Sichuan" },
			{ id: "smToolcnguizhou", name: "Guizhou" },
			{ id: "smToolcnyunnan", name: "Yunnan" },
			{ id: "smToolcntibet", name: "Tibet" },
			{ id: "smToolcnshaanxi", name: "Shaanxi" },
			{ id: "smToolcngansu", name: "Gansu" },
			{ id: "smToolcnqinghai", name: "Qinghai" },
			{ id: "smToolcnningxia", name: "Ningxia" },
			{ id: "smToolcnxinjiang", name: "Xinjiang" },
			{
				id: "smToolcnprovincial-levelpoliticalplatform",
				name: "Provincial-level Political Platform ",
			},
			{
				id: "smToolcnnational-centralnewsunit",
				name: "National - Central News Unit",
			},
			{ id: "smToolcnnational-expertmedia", name: "National - Expert Media " },
			{ id: "smToolcnnational-mediaplatforms", name: "National - Media Platforms" },
		],
		moeGraphAvailable: false,
		moeMapAvailable: false,
		overrideColors: true,
		showAllTopicsMap: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		// timezone: false,

		translateArticleTitles: false,
		uiThemeToggle: true,
		videoTabEntities: true,
		videoTabEntityGraph: true,
		videoTabStartDate: "08-01-2022", // "MM-DD-YYYY"
		videoTabTopics: false,
	},

	australia2022: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			crosstabs: true,
			narratives: true,
			bigMoversLink: false,
			curated: true,
			events: false,
			explorer: false,
			graph: false,
			media: true,
			moe: false,
			reportBuilderLink: false,
			social: true,
			video: true,
			web: false,
		},
		alwaysShowContentInteractions: true,
		// Default supports nested narratives with clickable main narrative
		compareHeaderConfig: false,
		contentColumns: contentColumnsAus22Only,
		defaultOriginalLanguage: true,
		defaultAggregationLevel: "weeklyTimeseries",
		defaultSearchTranslationEnabled: true,
		defaultSearchTranslationLanguages: ["zh"],
		// dmaMap: true,a
		topPhrasesAvailable: false,
		hideEntities: false,
		hideMap: false,
		entityFiltersAvailable: true,
		contentTabDateBegin: 1646114400000,
		mediaEntityFilters: false,
		mediaLanguages: ["All", "English", "Chinese"],
		mediaCountries: ["All", "Australia", "China"],
		googleTranslateLanguages: ["en", "zh-CN", "zh-TW"],
		exoveraEditButton: true,
		expandedContentShowTweets: true,
		fakeSingleArticle: false,
		graphDataTypes: [
			{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
			{ name: "Unique Article Count", id: "articleDedupCount", label: "Volume", perspectiveOverride: "Source" },
			{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
			// { name: "Article Reach", id: "articleReach", label: "Reach" },
		],
		hotArticleLimit: {
			hot: 1000,
			hotter: 5000,
			hottest: 10000,
		},
		configureStats: configureGECStats,
		defaultZoom: "1 year",
		hideParentNarrativeTags: false,
		highlightedEntitiesTitle: "Candidates",
		defaultLightUI: false,
		projectMapConfig: false,
		//   mediaLanguages: ["English", "Chinese"],
		// mapLookup: usFIPSLookup,
		socialAccounts: [
			{ name: "--", id: "all" },
			{ name: "Customer Accounts", id: "smToolCustomerList00" },
			{ name: "United Front Accounts", id: "smToolUnitedFront" },
			// { name: "Media Accounts", id: "smToolMedia" },
			{ name: "Chinese Media", id: "smToolChinaMedia" },
			{ name: "Australian Media", id: "smToolausMedia" },
			{ name: "PRC Government & Wolf Warriors", id: "smToolwolfWar" },
			{ name: "PLA", id: "smToolausPLA" },
			// { name: "Taliban", id: "aaaacccc-3333-4d2b-a01f-12378ddafa70" },
			// { name: "WeChat Only", id: "weChatOnly" },
			// { name: "Other Social", id: "otherSocial" },
		],
		socialPlatforms: [
			{ name: "--", id: "all" },
			{ name: "WeChat", id: "wechat" },
			{ name: "Weibo", id: "weibo" },
			{ name: "Twitter", id: "twitter" },
		],
		socialTopics: [
			{ name: "--", id: "all" },
			// { name: "All Topics", id: "allTracked" },
			{ name: "Australia Alliances", id: "alliancesTopic" },
			// { name: "Election", id: "electionTopic" },
			{ name: "Security", id: "securityTopic" },
			{ name: "Trade", id: "tradeTopic" },
			// { name: "Economics", id: "economicsTopic" },
			{ name: "Covid", id: "covidTopic" },
			{ name: "Discrimination", id: "racismTopic" },
			{ name: "Human Rights", id: "rightsTopic" },
			// { name: "Media", id: "mediaTopic" },
			// { name: "Immigration", id: "immigrationTopic" },
			{ name: "Australia in the Pacific", id: "pacificTopic" },
			// { name: "Environment", id: "pacificTopic" },
			{ name: "Australia in SE Asia", id: "seasiaTopic" },
			{ name: "PRC Official Travel", id: "prcofficialtravelTopic" },
			// { name: "Afghanistan", id: "d303b515-3333-4052-9603-889c5f823f10" },
			// { name: "RS/Nato", id: "e3adf36a-3333-41ca-8d84-ddc7d6cdc2cd" },
			// { name: "Other Regional", id: "b1976529-3333-44d4-82fd-4e4fc704808c" },
		],
		socialProvinces: [
			{ id: "all", name: "--" },
			{ id: "smToolcnshanxi", name: "Shanxi" },
			{ id: "smToolcninnermongolia", name: "Inner Mongolia " },
			{ id: "smToolcnliaoning", name: "Liaoning" },
			{ id: "smToolcnheilongjiang", name: "Heilongjiang" },
			{ id: "smToolcnjiangsu", name: "Jiangsu " },
			{ id: "smToolcnzhejiang", name: "Zhejiang" },
			{ id: "smToolcnanhui", name: "Anhui" },
			{ id: "smToolcnfujian", name: "Fujian" },
			{ id: "smToolcnjiangxi", name: "Jiangxi" },
			{ id: "smToolcnshandong", name: "Shandong" },
			{ id: "smToolcnhenan", name: "Henan" },
			{ id: "smToolcnhunan", name: "Hunan" },
			{ id: "smToolcnguangxi", name: "Guangxi" },
			{ id: "smToolcnhainan", name: "Hainan" },
			{ id: "smToolcnchongqing", name: "Chongqing" },
			{ id: "smToolcnsichuan", name: "Sichuan" },
			{ id: "smToolcnguizhou", name: "Guizhou" },
			{ id: "smToolcnyunnan", name: "Yunnan" },
			{ id: "smToolcntibet", name: "Tibet" },
			{ id: "smToolcnshaanxi", name: "Shaanxi" },
			{ id: "smToolcngansu", name: "Gansu" },
			{ id: "smToolcnqinghai", name: "Qinghai" },
			{ id: "smToolcnningxia", name: "Ningxia" },
			{ id: "smToolcnxinjiang", name: "Xinjiang" },
			{
				id: "smToolcnprovincial-levelpoliticalplatform",
				name: "Provincial-level Political Platform ",
			},
			{
				id: "smToolcnnational-centralnewsunit",
				name: "National - Central News Unit",
			},
			{ id: "smToolcnnational-expertmedia", name: "National - Expert Media " },
			{ id: "smToolcnnational-mediaplatforms", name: "National - Media Platforms" },
		],
		moeGraphAvailable: false,
		moeMapAvailable: false,
		overrideColors: true,
		showAllTopicsMap: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		// timezone: false,

		translateArticleTitles: false,
		uiThemeToggle: true,
		videoTabEntities: true,
		videoTabEntityGraph: true,
		videoTabStartDate: "08-01-2022", // "MM-DD-YYYY"
		videoTabTopics: false,
	},

	texas23: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			moe: true,
			narratives: true,
			bigMoversLink: false,
			curated: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			media: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		// Default supports nested narratives with clickable main narrative
		compareHeaderConfig: [
			{ id: "narratives", header: "Narratives" },
			{ id: "additionalSearches", header: "Other Trends" },
		],
		entityFiltersAvailable: true,
		fakeSingleArticle: false,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		hideEntities: false,
		hideParentNarrativeTags: false,
		defaultLightUI: false,
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		projectMapConfig: {
			narratives: {
				state: { name: "Texas 23rd", geoState: "texas23" },
			},
			compare: {
				state: { name: "Texas 23rd", geoState: "texas23" },
			},
			moe: {
				state: { name: "Texas 23rd", geoState: "texas23" },
			},
		},
		moeGraphAvailable: true,
		moeMapAvailable: true,
		overrideColors: true,
		showTopicsInGraph: false, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
	},

	// Cloned from GEC - gec
	balticsDemo: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			crosstabs: true,
			media: true, // need to set up api.dataKey
			narratives: true,
			bigMoversLink: false,
			curated: false,
			events: false,
			explorer: false,
			graph: false,
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		aiToggle: true,
		aiToggleNoReaction: true,
		alwaysShowContentInteractions: true,
		hideEntities: false,
		hideEntitiesInAIMode: true, // Linked to aiMode
		compareHeaderConfig: false,
		compareMultiSelect: true,
		configureStats: configureGECStats,
		contentColumns: contentColumnsGECOnly,
		// TODO: Install this and refactor summary-tab to consume it if the data ever support it
		// contentTabLabelPositions: {
		// 	topLeft: false,
		// 	topRight: "postDate",
		// 	bottomLeft: "featured",
		// 	bottomRight: false,
		// },
		contentTabTableHeaders: contentTabBase,
		defaultAggregationLevel: "weeklyTimeseries",
		defaultOriginalLanguage: true,
		defaultEntityType: "People",
		defaultSearchTranslationEnabled: true,
		defaultSearchTranslationLanguages: ["ru"],
		desiredCrosstabsNarrativeOrder: ["Russian", "Non-Russian", "Kremlin-independent", "Kremlin-aligned", "Grey/Unknown"],
		emailSubscribeOption: "BAMO-alerts",
		entityFiltersAvailable: true, // <-----------
		crosstabsBargraphTopLeft: {
			id: "Market",
			name: "Target Market",
			// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
			limit: 15, // optional
			// tooltipText: biasText, // optional
			// tooltipBullets: ["string", "string"], // optional - supported, just not in use currently
		},
		crosstabsBargraphMiddleLeft: {
			id: "Bias",
			name: "Source Orientation",
			limit: 15,
			// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
			tooltipText: biasText,
		},
		crosstabsBargraphMiddleBottomLeft: {
			id: "Credibility",
			name: "Credibility",
			limit: 15,
		},
		crosstabsBargraphBottomLeft: {
			id: "Language",
			name: "Source Language",
			limit: 15,
		},
		crosstabsBargraphTopRight: {
			id: "Persons",
			name: "People",
			limit: 25,
			tooltipText: peopleText,
		},
		// FLAG_DATA_HANDLING_FACET
		// crosstabsNarrativeFacets: [{ name: "Outlet Alignment", id: "default" }, { name: "Country", id: "country" }],
		crosstabsNarrativeHeaders: gecCrosstabsHeaders,
		crosstabsNarrativeHeaderLabels: [
			{
				groupBy: "lang",
				label: "Language",
				info: "Outlet language",
			},
			{
				groupBy: "alignment",
				label: "Kremlin Orientation",
				info: "Outlet Kremlin alignment",
			},
			{
				groupBy: "country",
				label: "Country",
				info: "Articles published within a given country",
			},
		],
		duplicateToggle: false,
		fakeSingleArticle: false,
		graphDataTypes: [
			{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
			{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
			// { name: "Article Reach", id: "articleReach", label: "Reach" },
		],
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false, // default to defaultLightUI?
		projectMapConfig: false,
		mapSourceTooltipText: "The deviation from the average number of published articles in a country that support a given narrative.",
		mapAudienceTooltipText: "The deviation from the average reaction to articles in a country that support a given narrative.",
		moeGraphAvailable: true,
		moeMapAvailable: true,
		moeTabName: "MOE",
		overrideColorsGently: true,
		projectSpecificTagFormat: formatGECTags,
		hideParentNarrativeTags: true,
		mediaLanguages: ["Latvian", "Russian", "Estonian", "Lithuanian", "Romanian"],
		// mediaPredictiveSelect: true,
		// mediaSearchHeaders: predictSearchHeaders,
		relevancySlider: false,
		showAllTopicsMap: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		statsPDF: true,
		timezone: false,
		translateArticleTitles: false,
		uiThemeToggle: true,
	},

	russianFocus: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			crosstabs: true,
			media: true, // need to set up api.dataKey
			narratives: true,
			bigMoversLink: false,
			curated: false,
			events: false,
			explorer: false,
			graph: false,
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		aiToggle: true,
		aiToggleNoReaction: true,
		alwaysShowContentInteractions: true,
		hideEntities: false,
		hideEntitiesInAIMode: true, // Linked to aiMode
		compareHeaderConfig: false,
		compareMultiSelect: true,
		configureStats: configureGECStats,
		contentColumns: contentColumnsGECOnly,
		// TODO: Install this and refactor summary-tab to consume it if the data ever support it
		// contentTabLabelPositions: {
		//      topLeft: false,
		//      topRight: "postDate",
		//      bottomLeft: "featured",
		//      bottomRight: false,
		// },
		contentTabTableHeaders: contentTabBase,
		defaultAggregationLevel: "weeklyTimeseries",
		defaultOriginalLanguage: true,
		defaultEntityType: "People",
		defaultSearchTranslationEnabled: true,
		defaultSearchTranslationLanguages: ["ru"],
		desiredCrosstabsNarrativeOrder: ["Russian", "Non-Russian", "Kremlin-independent", "Kremlin-aligned", "Grey/Unknown"],
		emailSubscribeOption: "BAMO-alerts",
		entityFiltersAvailable: true, // <-----------
		crosstabsBargraphTopLeft: {
			id: "Market",
			name: "Target Market",
			// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
			limit: 15, // optional
			// tooltipText: biasText, // optional
			// tooltipBullets: ["string", "string"], // optional - supported, just not in use currently
		},
		crosstabsBargraphMiddleLeft: {
			id: "Bias",
			name: "Source Orientation",
			limit: 15,
			// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
			tooltipText: biasText,
		},
		crosstabsBargraphMiddleBottomLeft: {
			id: "Credibility",
			name: "Credibility",
			limit: 15,
		},
		crosstabsBargraphBottomLeft: {
			id: "Language",
			name: "Source Language",
			limit: 15,
		},
		crosstabsBargraphTopRight: {
			id: "Persons",
			name: "People",
			limit: 25,
			tooltipText: peopleText,
		},
		// FLAG_DATA_HANDLING_FACET
		// crosstabsNarrativeFacets: [{ name: "Outlet Alignment", id: "default" }, { name: "Country", id: "country" }],
		crosstabsNarrativeHeaders: gecCrosstabsHeaders,
		crosstabsNarrativeHeaderLabels: [
			{
				groupBy: "lang",
				label: "Language",
				info: "Outlet language",
			},
			{
				groupBy: "alignment",
				label: "Kremlin Orientation",
				info: "Outlet Kremlin alignment",
			},
			{
				groupBy: "country",
				label: "Country",
				info: "Articles published within a given country",
			},
		],
		duplicateToggle: false,
		fakeSingleArticle: false,
		graphDataTypes: [
			{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
			{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
			// { name: "Article Reach", id: "articleReach", label: "Reach" },
		],
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false, // default to defaultLightUI?
		projectMapConfig: false,
		mapSourceTooltipText: "The deviation from the average number of published articles in a country that support a given narrative.",
		mapAudienceTooltipText: "The deviation from the average reaction to articles in a country that support a given narrative.",
		moeGraphAvailable: true,
		moeMapAvailable: true,
		moeTabName: "MOE",
		overrideColorsGently: true,
		projectSpecificTagFormat: formatGECTags,
		hideParentNarrativeTags: true,
		mediaLanguages: ["Latvian", "Russian", "Estonian", "Lithuanian", "Romanian"],
		// mediaPredictiveSelect: true,
		// mediaSearchHeaders: predictSearchHeaders,
		relevancySlider: false,
		showAllTopicsMap: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		statsPDF: true,
		timezone: false,
		translateArticleTitles: false,
		uiThemeToggle: true,
	},

	bcbs: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			narratives: true,
			bigMoversLink: false,
			curated: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			media: false,
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		compareHeaderConfig: false,
		contentColumns: noTypeContentColumns,
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		disableFilters: ["audienceGender", "audiencePolitics", "audienceType"],
		disableFiltersMessage:
			"Models for individual account types, including gender, political leaning, and account type are currently being processed, please check back later.",
		duplicateToggle: true,
		entityFiltersAvailable: false,
		expandedContentShowTweets: true,
		fakeSingleArticle: false,
		hideEntities: false,
		hideMapIndicatorInGraph: true,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: true,
		projectMapConfig: false,
		moeGraphAvailable: false, // <-----------
		moeMapAvailable: true,
		overrideColors: true,
		showAllTopicsMap: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
	},

	dccc: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			narratives: true,
			bigMoversLink: false,
			curated: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			media: false,
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		compareHeaderConfig: false,
		contentColumns: noTypeContentColumns,
		// disableFilters: ["audienceGender", "audiencePolitics", "audienceType"],
		// disableFiltersMessage:
		// 	"Models for individual account types, including gender, political leaning, and account type are currently being processed, please check back later.",
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		duplicateToggle: true,
		entityFiltersAvailable: false,
		expandedContentShowTweets: true,
		fakeSingleArticle: false,
		hideEntities: false,
		hideMapIndicatorInGraph: true,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: true,
		projectMapConfig: false,
		moeGraphAvailable: false, // <-----------
		moeMapAvailable: true,
		overrideColors: true,
		showAllTopicsMap: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
	},

	midterms: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			moe: true,
			narratives: true,
			bigMoversLink: false,
			curated: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			media: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		compareHeaderConfig: false,
		contentColumns: noTypeContentColumns,
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		duplicateToggle: true,
		entityFiltersAvailable: false,
		fakeSingleArticle: false,
		hideEntities: false,
		hideMapIndicatorInGraph: true,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: true,
		projectMapConfig: false,
		moeGraphAvailable: false, // <-----------
		moeMapAvailable: true,
		noReaction: true,
		overrideColors: true,
		showAllTopicsMap: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
	},

	iraq: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			narratives: true,
			bigMoversLink: false,
			curated: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			media: false,
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		compareHeaderConfig: false,
		entityFiltersAvailable: false, // <-----------
		fakeSingleArticle: true, // <----------- using USCC endpoint too
		hideEntities: false,
		hotArticleLimit: {
			hot: 1000,
			hotter: 2000,
			hottest: 5000,
		},
		defaultLightUI: true,
		defaultSearchTranslationEnabled: true,
		defaultSearchTranslationLanguages: ["fa"],
		projectMapConfig: false,
		moeGraphAvailable: false, // <-----------
		moeMapAvailable: true,
		overrideColors: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
	},

	cbp: {
		availableTabs: {
			default: "curated",
			asksage: false,
			curated: true,
			graph: true,
			bigMoversLink: false,
			crosstabs: false,
			events: false,
			explorer: false,
			media: false,
			moe: false,
			narratives: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		// additionalLegendHeaders: [
		// 	{
		// 		name: "Predictive",
		// 		id: "predictiveVal",
		// 		hideOnSmall: true,
		// 		width: 85,
		// 		minWidth: 800,
		// 	},
		// 	{
		// 		name: "Optimal Lag",
		// 		id: "optimalLag",
		// 		hideOnSmall: true,
		// 		width: 80,
		// 		minWidth: 800,
		// 		transform: lagTransform,
		// 	},
		// ],
		compareHeaderConfig: false,
		contentTabCsvHeaders: [
			{
				name: "Title",
				id: "title",
			},
			{
				name: "Date",
				id: "postDate",
			},
			{
				name: "Analysis",
				id: "analysis",
			},
			{
				name: "Analysis Date",
				id: "analysisDate",
			},
			{
				name: "Impact Horizon",
				id: "impactHorizon",
			},
			{
				name: "Push Pull",
				id: "pushPull",
			},
			{
				name: "Signal Type",
				id: "signalType",
			},
		],
		contentTabTableHeaders: contentTabPushPullCountry,
		contentTabMap: false,
		contentTabDateBegin: 1559390400000,
		contentTabDateEnd: 1569844800000,
		defaultZoom: "1 year",
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [""],
		entityFiltersAvailable: true, // <-----------
		fakeSingleArticle: false,
		// graphTabFacets: [
		// 	{
		// 		name: "Gender",
		// 		id: "gender",
		// 		width: 83,
		// 		options: [
		// 			{
		// 				name: "All",
		// 				id: "gender_all",
		// 				parent: "gender",
		// 			},
		// 			{
		// 				name: "Male",
		// 				id: "gender_male",
		// 				parent: "gender",
		// 			},
		// 			{
		// 				name: "Female",
		// 				id: "gender_female",
		// 				parent: "gender",
		// 			},
		// 		],
		// 	},
		// 	{
		// 		name: "UAC",
		// 		id: "uac",
		// 		info: "Unaccompanied Alien Children",
		// 		width: 60,
		// 		options: [
		// 			{
		// 				name: "All",
		// 				id: "uac_all",
		// 				parent: "uac",
		// 			},
		// 			{
		// 				name: "Yes",
		// 				id: "uac_yes",
		// 				parent: "uac",
		// 			},
		// 			{
		// 				name: "No",
		// 				id: "uac_no",
		// 				parent: "uac",
		// 			},
		// 		],
		// 	},
		// 	{
		// 		name: "Citizenship",
		// 		id: "citizenship",
		// 		width: 108,
		// 		options: [
		// 			{
		// 				name: "All",
		// 				id: "citizenship_all",
		// 				parent: "citizenship",
		// 			},
		// 			{
		// 				name: "Guatemala",
		// 				id: "citizenship_guatemala",
		// 				parent: "citizenship",
		// 			},
		// 			{
		// 				name: "Honduras",
		// 				id: "citizenship_honduras",
		// 				parent: "citizenship",
		// 			},
		// 			{
		// 				name: "El Salvador",
		// 				id: "citizenship_el_salvador",
		// 				parent: "citizenship",
		// 			},
		// 		],
		// 	},
		// ],
		graphTabSelectorColumns: [
			{
				name: "Search",
				id: "name",
				width: 3,
				fixed: false,
				transform: addLineHeightTransform,
			},
			{
				name: "Location",
				id: "locationStr",
				width: 100,
				fixed: true,
			},
			{
				name: "Immediate",
				id: "immediate",
				type: "prediction", // Used for local filtering
				width: 115,
				fixed: true,
				paddingLeft: 20,
				reverseSort: true,
				headerTransform: changeHeaderTransform,
				transform: changeTransform,
				sortColumn: sortChange,
			},
			{
				name: "Immediate Confidence",
				id: "immediate",
				type: "prediction", // Used for local filtering
				width: 60,
				paddingLeft: 10,
				fixed: true,
				reverseSort: true,
				headerTransform: confidenceHeaderTransform,
				transform: confidenceTransform,
				sortColumn: sortConfidence,
			},
			{
				name: "Short",
				id: "short",
				type: "prediction", // Used for local filtering
				width: 80,
				fixed: true,
				paddingLeft: 20,
				reverseSort: true,
				headerTransform: changeHeaderTransform,
				transform: changeTransform,
				sortColumn: sortChange,
			},
			{
				name: "Short Confidence",
				id: "short",
				type: "prediction", // Used for local filtering
				width: 60,
				paddingLeft: 10,
				fixed: true,
				reverseSort: true,
				headerTransform: confidenceHeaderTransform,
				transform: confidenceTransform,
				sortColumn: sortConfidence,
			},
			{
				name: "Medium",
				id: "mid",
				type: "prediction", // Used for local filtering
				width: 95,
				fixed: true,
				paddingLeft: 20,
				reverseSort: true,
				headerTransform: changeHeaderTransform,
				transform: changeTransform,
				sortColumn: sortChange,
			},
			{
				name: "Medium Confidence",
				id: "mid",
				type: "prediction", // Used for local filtering
				width: 60,
				paddingLeft: 10,
				fixed: true,
				reverseSort: true,
				headerTransform: confidenceHeaderTransform,
				transform: confidenceTransform,
				sortColumn: sortConfidence,
			},
			{
				name: "Long",
				id: "long",
				type: "prediction", // Used for local filtering
				width: 75,
				fixed: true,
				paddingLeft: 20,
				reverseSort: true,
				headerTransform: changeHeaderTransform,
				transform: changeTransform,
				sortColumn: sortChange,
			},
			{
				name: "Long Confidence",
				id: "long",
				type: "prediction", // Used for local filtering
				width: 60,
				paddingLeft: 10,
				fixed: true,
				reverseSort: true,
				headerTransform: confidenceHeaderTransform,
				transform: confidenceTransform,
				sortColumn: sortConfidence,
			},
		],
		hideEntities: false,
		hideParentNarrativeTags: true,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false,
		projectMapConfig: false,
		// methodologyLink: "src/static/CBP_Methodology.docx",
		moeGraphAvailable: true,
		moeMapAvailable: true,
		moeTabName: "MOE",
		overrideColors: true,
		percentageTypes: ["predata"],
		// projectNotifications: [
		// 	{
		// 		// notificationDate
		// 		notificationMessage:
		// 			"The data is currently updating. There may be some inconsistencies until it is complete.",
		// 		notificationType: "maintenance",
		// 		title: "Data update underway",
		// 		uuid: "cbpLocal_1",
		// 	},
		// ],
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
	},

	pnnl: {
		availableTabs: {
			default: "video",
			asksage: false,
			crosstabs: false,
			explorer: true,
			curated: false,
			media: true, // need to set up api.dataKey
			narratives: false,
			social: true,
			video: true, // set api.videoFilters
			web: true,
			bigMoversLink: false,
			events: false,
			graph: false,
			moe: false,
			reportBuilderLink: false,
		},
		alwaysShowContentInteractions: false,
		// Default supports nested narratives with clickable main narrative
		compareHeaderConfig: false,
		contentColumns: baseContentColumns,
		contentTabCsvHeaders: [
			{
				name: "Title",
				id: "title",
			},
			{
				name: "Source",
				id: "source",
			},
			{
				name: "Date",
				id: "postDate",
			},
		],
		contentTabTableHeaders: contentTabBase,
		contentTabMap: false,
		defaultSearchTranslationEnabled: true,
		defaultSearchTranslationLanguages: ["zh"],
		// defaultAggregationLevel: "weeklyTimeseries", // defaults to "timeseries"
		desiredCrosstabsNarrativeOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"],
		// dmaMap: true,
		entityFiltersAvailable: true,
		crosstabsBargraphTopLeft: {
			id: "Organizations",
			name: "Organizations",
			// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
			limit: 10, // optional
			// tooltipText: biasText, // optional
			// tooltipBullets: ["string", "string"], // optional - supported, just not in use currently
			// disableMax: true, // FLAG_DISABLE_MAX - unsupported for now. will disable left side chartMax if any pass this (implementation sounds clunky - may want to rethink it as an additional config item)
		},
		crosstabsBargraphMiddleLeft: {
			id: "Companies",
			name: "Companies",
			limit: 10,
		},
		// crosstabsBargraphBottomLeft:
		crosstabsBargraphTopRight: {
			id: "Persons",
			name: "People",
			limit: 25,
			tooltipText: peopleText,
		},
		// crosstabsBargraphMiddleRight:
		// crosstabsBargraphBottomRight:
		// FLAG_DATA_HANDLING_FACET
		crosstabsNarrativeFacets: [
			{ name: "Political Bias", id: "default" },
			{ name: "State Leaning", id: "stateLeaning" },
		],
		crosstabsNarrativeHeaders: us2020CrosstabsHeaders,
		crosstabsNarrativeHeaderLabels: [
			{
				groupBy: "bias",
				label: "Political Bias",
				info: "Articles published by outlets, grouped by political bias",
			},
			{
				groupBy: "stateLeaning",
				label: "State Political Leaning",
				info: "Articles published in states, grouped by their projected political alignment in the 2020 election",
				bullets: [
					"Battleground (13): CO, FL, IA, ME, MI, MN, NC, NH, NV, OH, PA, VA, WI",
					"Left (19): CA, CT, DC, DE, HI, IL, MA, MD, NM, NJ, NY, OR, RI, VT, WA",
					"Right (21): AK, AL, AR, AZ, GA, ID, IN, KS, KY, LA, MO, OK, MS, MT, NE, ND, SC, SD, TN, TX, UT, WV, WY",
				],
			},
		],
		// exoveraEditButton: true,
		expandedContentShowTweets: false,
		fakeSingleArticle: false,
		graphDataTypes: [
			{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
			{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
			// { name: "Article Reach", id: "articleReach", label: "Reach" },
		],
		hotArticleLimit: {
			hot: 1000,
			hotter: 5000,
			hottest: 10000,
		},
		hideEntities: false,
		hideMap: true,
		hideParentNarrativeTags: false,
		highlightedEntitiesTitle: "Candidates",
		defaultLightUI: false,
		projectMapConfig: false,
		// mapLookup: usFIPSLookup,
		mediaEntityFilters: false,
		userSearchHideLocation: true,
		explorerDatasets: [
			{ name: "Procurement", id: "procurement", tableColumns: ["title"] },
			{
				name: "Entities",
				id: "rover",
				tableColumns: [
					"label_en",
					"label",
					// "aka",
					"entity_type",
					"categories",
					"primary_country",
					"sanctioned",
					// "sanction_authority",
					// "sanction_program",
				],
			},
			{ name: "Corporate Records", id: "corprecords", tableColumns: ["label", "business_scope", "industry", "enterprise_type"] },
			{ name: "Resumes", id: "resumes", tableColumns: ["name", "gender", "current_position", "age"] },
		],
		explorerShowEntities: ["procurement"],
		mediaLanguages: ["English", "Chinese"],
		googleTranslateLanguages: ["en", "zh-CN", "zh-TW"],
		mediaTranslateTitles: false,
		moeGraphAvailable: false,
		moeMapAvailable: false,
		narrativeEntityTranslateToggle: false,
		overrideColors: true,
		showAllTopicsMap: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
		topPhrasesAvailable: false,
		translateArticleTitles: false,
		uiThemeToggle: true,
		// videoTabEndDate: "05-14-2020", // "MM-DD-YYYY"
		videoTabEntities: true,
		videoTabEntityGraph: true,
		videoTabStartDate: "01-01-2020", // "MM-DD-YYYY"
		videoTabTopics: false,
	},

	china: {
		availableTabs: {
			default: "video",
			asksage: false,
			crosstabs: false,
			explorer: true,
			media: true, // need to set up api.dataKey
			narratives: true,
			social: true,
			video: true, // set api.videoFilters
			web: true,
			bigMoversLink: false,
			curated: true,
			events: false,
			graph: false,
			moe: false,
			reportBuilderLink: false,
		},
		alwaysShowContentInteractions: true,
		// Default supports nested narratives with clickable main narrative
		compareHeaderConfig: false,
		contentColumns: baseContentColumns,
		contentTabCsvHeaders: [
			{
				name: "Title",
				id: "title",
			},
			{
				name: "Source",
				id: "source",
			},
			{
				name: "Date",
				id: "postDate",
			},
		],
		contentTabTableHeaders: contentTabBase,
		contentTabMap: false,
		contentTabDateBegin: 1577836800000,
		// defaultAggregationLevel: "weeklyTimeseries", // defaults to "timeseries"
		desiredCrosstabsNarrativeOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"],
		// dmaMap: true,
		entityFiltersAvailable: true,
		crosstabsBargraphTopLeft: {
			id: "Organizations",
			name: "Organizations",
			// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
			limit: 10, // optional
			// tooltipText: biasText, // optional
			// tooltipBullets: ["string", "string"], // optional - supported, just not in use currently
			// disableMax: true, // FLAG_DISABLE_MAX - unsupported for now. will disable left side chartMax if any pass this (implementation sounds clunky - may want to rethink it as an additional config item)
		},
		crosstabsBargraphMiddleLeft: {
			id: "Companies",
			name: "Companies",
			limit: 10,
		},
		// crosstabsBargraphBottomLeft:
		crosstabsBargraphTopRight: {
			id: "Persons",
			name: "People",
			limit: 25,
			tooltipText: peopleText,
		},
		// crosstabsBargraphMiddleRight:
		// crosstabsBargraphBottomRight:
		// FLAG_DATA_HANDLING_FACET
		crosstabsNarrativeFacets: [
			{ name: "Political Bias", id: "default" },
			{ name: "State Leaning", id: "stateLeaning" },
		],
		crosstabsNarrativeHeaders: us2020CrosstabsHeaders,
		crosstabsNarrativeHeaderLabels: [
			{
				groupBy: "bias",
				label: "Political Bias",
				info: "Articles published by outlets, grouped by political bias",
			},
			{
				groupBy: "stateLeaning",
				label: "State Political Leaning",
				info: "Articles published in states, grouped by their projected political alignment in the 2020 election",
				bullets: [
					"Battleground (13): CO, FL, IA, ME, MI, MN, NC, NH, NV, OH, PA, VA, WI",
					"Left (19): CA, CT, DC, DE, HI, IL, MA, MD, NM, NJ, NY, OR, RI, VT, WA",
					"Right (21): AK, AL, AR, AZ, GA, ID, IN, KS, KY, LA, MO, OK, MS, MT, NE, ND, SC, SD, TN, TX, UT, WV, WY",
				],
			},
		],
		defaultSearchTranslationEnabled: true,
		defaultSearchTranslationLanguages: ["zh"],
		// exoveraEditButton: true,
		expandedContentShowTweets: false,
		fakeSingleArticle: false,
		graphDataTypes: [
			{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
			{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
			// { name: "Article Reach", id: "articleReach", label: "Reach" },
		],
		hotArticleLimit: {
			hot: 1000,
			hotter: 5000,
			hottest: 10000,
		},
		hideEntities: true, // Temporary disabling of entities in narratives tab due to NER not working in Chinese
		hideMap: false,
		hideParentNarrativeTags: false,
		highlightedEntitiesTitle: "Candidates",
		defaultLightUI: false,
		projectMapConfig: false,
		// mapLookup: usFIPSLookup,
		mediaEntityFilters: false,
		userSearchHideLocation: true,
		mediaLanguages: ["English", "Chinese"],
		googleTranslateLanguages: ["en", "zh-CN", "zh-TW"],
		mediaTranslateTitles: false,
		moeGraphAvailable: false,
		explorerDatasets: [
			{ name: "Procurement", id: "procurement", tableColumns: ["title", "province", "sourceUrl"] },
			{
				name: "Entities",
				id: "rover",
				tableColumns: [
					"label_en",
					"label",
					// "aka",
					"entity_type",
					"categories",
					"primary_country",
					"sanctioned",
					// "sanction_authority",
					// "sanction_program",
				],
			},
			{ name: "Corporate Records", id: "corprecords", tableColumns: ["label", "business_scope", "industry", "enterprise_type"] },
			{ name: "Resumes", id: "resumes", tableColumns: ["name", "gender", "current_position", "age"] },
			{
				name: "Russia Contracts",
				id: "contract_awards",
				tableColumns: [
					"contractUrl",
					"price",
					"customerINN",
					"customerName",
					"customerKPP",
					"contractNumber",
					"contractType",
					"regionCode",
				],
			},
			{ name: "Publications", id: "jbn", tableColumns: ["type", "source", "title", "author", "date", "totalDocumentTermsCount"] },
		],
		// Temporary disabling of entities display due to NER not working in Chinese
		// explorerShowEntities: ["procurement"], 
		socialAccounts: [
			{ name: "--", id: "all" },
			{ name: "Customer Accounts", id: "smToolCustomerList00" },
			{ name: "United Front Accounts", id: "smToolUnitedFront" },
			// { name: "Media Accounts", id: "smToolMedia" },
			{ name: "Chinese Media", id: "smToolChinaMedia" },
			{ name: "PRC Government & Wolf Warriors", id: "smToolwolfWar" },
			{ name: "PLA", id: "smToolausPLA" },
			// { name: "Taliban", id: "aaaacccc-3333-4d2b-a01f-12378ddafa70" },
			// { name: "WeChat Only", id: "weChatOnly" },
			// { name: "Other Social", id: "otherSocial" },
		],
		socialPlatforms: [
			{ name: "--", id: "all" },
			{ name: "WeChat", id: "wechat" },
			{ name: "Weibo", id: "weibo" },
			{ name: "Twitter", id: "twitter" },
		],
		socialTopics: [
			{ name: "--", id: "all" },
			// { name: "All Topics", id: "allTracked" },
			// { name: "Australia Alliances", id: "alliancesTopic" },
			// { name: "Election", id: "electionTopic" },
			{ name: "Security", id: "securityTopic" },
			{ name: "Trade", id: "tradeTopic" },
			// { name: "Economics", id: "economicsTopic" },
			{ name: "Covid", id: "covidTopic" },
			{ name: "Discrimination", id: "racismTopic" },
			{ name: "Human Rights", id: "rightsTopic" },
			// { name: "Media", id: "mediaTopic" },
			// { name: "Immigration", id: "immigrationTopic" },
			// { name: "Australia in the Pacific", id: "pacificTopic" },
			// { name: "Environment", id: "pacificTopic" },
			// { name: "Australia in SE Asia", id: "seasiaTopic" },
			{ name: "PRC Official Travel", id: "prcofficialtravelTopic" },
			// { name: "Afghanistan", id: "d303b515-3333-4052-9603-889c5f823f10" },
			// { name: "RS/Nato", id: "e3adf36a-3333-41ca-8d84-ddc7d6cdc2cd" },
			// { name: "Other Regional", id: "b1976529-3333-44d4-82fd-4e4fc704808c" },
		],
		socialProvinces: [
			{ id: "all", name: "--" },
			{ id: "smToolcnshanxi", name: "Shanxi" },
			{ id: "smToolcninnermongolia", name: "Inner Mongolia " },
			{ id: "smToolcnliaoning", name: "Liaoning" },
			{ id: "smToolcnheilongjiang", name: "Heilongjiang" },
			{ id: "smToolcnjiangsu", name: "Jiangsu " },
			{ id: "smToolcnzhejiang", name: "Zhejiang" },
			{ id: "smToolcnanhui", name: "Anhui" },
			{ id: "smToolcnfujian", name: "Fujian" },
			{ id: "smToolcnjiangxi", name: "Jiangxi" },
			{ id: "smToolcnshandong", name: "Shandong" },
			{ id: "smToolcnhenan", name: "Henan" },
			{ id: "smToolcnhunan", name: "Hunan" },
			{ id: "smToolcnguangxi", name: "Guangxi" },
			{ id: "smToolcnhainan", name: "Hainan" },
			{ id: "smToolcnchongqing", name: "Chongqing" },
			{ id: "smToolcnsichuan", name: "Sichuan" },
			{ id: "smToolcnguizhou", name: "Guizhou" },
			{ id: "smToolcnyunnan", name: "Yunnan" },
			{ id: "smToolcntibet", name: "Tibet" },
			{ id: "smToolcnshaanxi", name: "Shaanxi" },
			{ id: "smToolcngansu", name: "Gansu" },
			{ id: "smToolcnqinghai", name: "Qinghai" },
			{ id: "smToolcnningxia", name: "Ningxia" },
			{ id: "smToolcnxinjiang", name: "Xinjiang" },
			{
				id: "smToolcnprovincial-levelpoliticalplatform",
				name: "Provincial-level Political Platform ",
			},
			{
				id: "smToolcnnational-centralnewsunit",
				name: "National - Central News Unit",
			},
			{ id: "smToolcnnational-expertmedia", name: "National - Expert Media " },
			{ id: "smToolcnnational-mediaplatforms", name: "National - Media Platforms" },
		],
		socialContains: [
			{ name: "Any", id: "all" },
			{ name: "Images", id: "smToolContainsImages" },
			{ name: "Videos", id: "smToolContainsVideos" },
		],
		moeMapAvailable: false,
		narrativeEntityTranslateToggle: false,
		overrideColors: true,
		showAllTopicsMap: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
		topPhrasesAvailable: false,
		translateArticleTitles: false,
		uiThemeToggle: true,
		// videoTabEndDate: "05-14-2020", // "MM-DD-YYYY"
		videoTabEntities: true,
		videoTabEntityGraph: true,
		videoTabStartDate: "01-01-2020", // "MM-DD-YYYY"
		videoTabTopics: false,
	},

	craftsman: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			bigMoversLink: "/bigmovers/index.html",
			events: true,
			moe: true,
			narratives: true,
			curated: false,
			crosstabs: false,
			explorer: false,
			graph: false,
			media: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		compareHeaderConfig: false,
		contentHeaderSortAudienceDefault: "fdate", // non-config default is "shares"
		emailSubscribeOption: false,
		entityFiltersAvailable: true, // <-----------
		eventColumns: noSharesContentColumns,
		fakeSingleArticle: false,
		hideEntities: false,
		hideParentNarrativeTags: true,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false,
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		projectMapConfig: false,
		moeGraphAvailable: true,
		moeMapAvailable: true,
		moeTabName: "MOE",
		overrideColors: true, // <-------
		showAllTopicsMap: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270, displayInSourceTimeZone: true },
	},

	oseRussia: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			crosstabs: true,
			media: true, // need to set up api.dataKey
			narratives: true,
			bigMoversLink: false,
			curated: false,
			events: false,
			explorer: true,
			graph: false,
			moe: false,
			reportBuilderLink: false,
			social: true,
			video: false,
			web: true,
		},
		aiToggle: true,
		aiToggleNoReaction: true,
		alwaysShowContentInteractions: true,
		hideEntities: false,
		hideEntitiesInAIMode: true, // Linked to aiMode
		compareHeaderConfig: false,
		compareMultiSelect: true,
		configureStats: configureGECStats,
		contentColumns: contentColumnsGECOnly,
		// TODO: Install this and refactor summary-tab to consume it if the data ever support it
		// contentTabLabelPositions: {
		// 	topLeft: false,
		// 	topRight: "postDate",
		// 	bottomLeft: "featured",
		// 	bottomRight: false,
		// },
		contentTabTableHeaders: contentTabBase,
		defaultAggregationLevel: "weeklyTimeseries",
		defaultOriginalLanguage: true,
		defaultEntityType: "People",
		defaultSearchTranslationEnabled: true,
		defaultSearchTranslationLanguages: ["ru"],
		desiredCrosstabsNarrativeOrder: ["Russian", "Non-Russian", "Kremlin-independent", "Kremlin-aligned", "Grey/Unknown"],
		emailSubscribeOption: "BAMO-alerts",
		entityFiltersAvailable: true, // <-----------
		expandedContentShowTweets: true,
		crosstabsBargraphTopLeft: {
			id: "Market",
			name: "Target Market",
			// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
			limit: 15, // optional
			// tooltipText: biasText, // optional
			// tooltipBullets: ["string", "string"], // optional - supported, just not in use currently
		},
		crosstabsBargraphMiddleLeft: {
			id: "Bias",
			name: "Source Orientation",
			limit: 15,
			// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
			tooltipText: biasText,
		},
		crosstabsBargraphMiddleBottomLeft: {
			id: "Credibility",
			name: "Credibility",
			limit: 15,
		},
		crosstabsBargraphBottomLeft: {
			id: "Language",
			name: "Source Language",
			limit: 15,
		},
		crosstabsBargraphTopRight: {
			id: "Persons",
			name: "People",
			limit: 25,
			tooltipText: peopleText,
		},
		// FLAG_DATA_HANDLING_FACET
		// crosstabsNarrativeFacets: [{ name: "Outlet Alignment", id: "default" }, { name: "Country", id: "country" }],
		crosstabsNarrativeHeaders: gecCrosstabsHeaders,
		crosstabsNarrativeHeaderLabels: [
			{
				groupBy: "lang",
				label: "Language",
				info: "Outlet language",
			},
			{
				groupBy: "alignment",
				label: "Kremlin Orientation",
				info: "Outlet Kremlin alignment",
			},
			{
				groupBy: "country",
				label: "Country",
				info: "Articles published within a given country",
			},
		],
		duplicateToggle: false,
		fakeSingleArticle: false,
		graphDataTypes: [
			{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
			{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
			// { name: "Article Reach", id: "articleReach", label: "Reach" },
		],
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		socialPlatforms: [
			{ name: "Any", id: "all" },
			{ name: "Telegram", id: "telegram" },
			{ name: "Twitter", id: "twitter" },
		],
		socialContains: [
			{ name: "Any", id: "all" },
			{ name: "Images", id: "smToolContainsImages" },
			{ name: "Videos", id: "smToolContainsVideos" },
		],
		explorerDatasets: [
			{
				name: "Entities",
				id: "rover",
				tableColumns: [
					"label_en",
					"label",
					// "aka",
					"entity_type",
					"categories",
					"primary_country",
					"sanctioned",
					// "sanction_authority",
					// "sanction_program",
				],
			},
			{
				name: "Russia Contracts",
				id: "contract_awards",
				tableColumns: [
					"contractUrl",
					"price",
					"customerINN",
					"customerName",
					"customerKPP",
					"contractNumber",
					"contractType",
					"regionCode",
				],
			},
			{ name: "Publications", id: "jbn", tableColumns: ["type", "source", "title", "author", "date", "totalDocumentTermsCount"] },
		],
		defaultLightUI: false, // default to defaultLightUI?
		projectMapConfig: false,
		mapSourceTooltipText: "The deviation from the average number of published articles in a country that support a given narrative.",
		mapAudienceTooltipText: "The deviation from the average reaction to articles in a country that support a given narrative.",
		moeGraphAvailable: true,
		moeMapAvailable: true,
		moeTabName: "MOE",
		overrideColorsGently: true,
		projectSpecificTagFormat: formatGECTags,
		hideParentNarrativeTags: true,
		mediaLanguages: ["Latvian", "Russian", "Estonian", "Lithuanian", "Romanian"],
		// mediaPredictiveSelect: true,
		// mediaSearchHeaders: predictSearchHeaders,
		relevancySlider: false,
		showAllTopicsMap: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		statsPDF: true,
		timezone: false,
		translateArticleTitles: false,
		uiThemeToggle: true,
	},

	gec: {
		availableTabs: {
			asksage: false,
			default: "narratives",
			crosstabs: true,
			media: true, // need to set up api.dataKey
			narratives: true,
			bigMoversLink: false,
			curated: false,
			events: false,
			explorer: false,
			graph: false,
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		aiToggle: true,
		aiToggleNoReaction: true,
		alwaysShowContentInteractions: true,
		hideEntities: false,
		hideEntitiesInAIMode: true, // Linked to aiMode
		compareHeaderConfig: false,
		compareMultiSelect: true,
		configureStats: configureGECStats,
		contentColumns: contentColumnsGECOnly,
		// TODO: Install this and refactor summary-tab to consume it if the data ever support it
		// contentTabLabelPositions: {
		// 	topLeft: false,
		// 	topRight: "postDate",
		// 	bottomLeft: "featured",
		// 	bottomRight: false,
		// },
		contentTabTableHeaders: contentTabBase,
		defaultAggregationLevel: "weeklyTimeseries",
		defaultOriginalLanguage: true,
		defaultEntityType: "People",
		defaultSearchTranslationEnabled: true,
		defaultSearchTranslationLanguages: ["ru"],
		desiredCrosstabsNarrativeOrder: ["Russian", "Non-Russian", "Kremlin-independent", "Kremlin-aligned", "Grey/Unknown"],
		emailSubscribeOption: "BAMO-alerts",
		entityFiltersAvailable: true, // <-----------
		expandedContentShowTweets: true,
		crosstabsBargraphTopLeft: {
			id: "Market",
			name: "Target Market",
			// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
			limit: 15, // optional
			// tooltipText: biasText, // optional
			// tooltipBullets: ["string", "string"], // optional - supported, just not in use currently
		},
		crosstabsBargraphMiddleLeft: {
			id: "Bias",
			name: "Source Orientation",
			limit: 15,
			// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
			tooltipText: biasText,
		},
		crosstabsBargraphMiddleBottomLeft: {
			id: "Credibility",
			name: "Credibility",
			limit: 15,
		},
		crosstabsBargraphBottomLeft: {
			id: "Language",
			name: "Source Language",
			limit: 15,
		},
		crosstabsBargraphTopRight: {
			id: "Persons",
			name: "People",
			limit: 25,
			tooltipText: peopleText,
		},
		// FLAG_DATA_HANDLING_FACET
		// crosstabsNarrativeFacets: [{ name: "Outlet Alignment", id: "default" }, { name: "Country", id: "country" }],
		crosstabsNarrativeHeaders: gecCrosstabsHeaders,
		crosstabsNarrativeHeaderLabels: [
			{
				groupBy: "lang",
				label: "Language",
				info: "Outlet language",
			},
			{
				groupBy: "alignment",
				label: "Kremlin Orientation",
				info: "Outlet Kremlin alignment",
			},
			{
				groupBy: "country",
				label: "Country",
				info: "Articles published within a given country",
			},
		],
		duplicateToggle: false,
		fakeSingleArticle: false,
		graphDataTypes: [
			{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
			{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
			// { name: "Article Reach", id: "articleReach", label: "Reach" },
		],
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false, // default to defaultLightUI?
		projectMapConfig: false,
		mapSourceTooltipText: "The deviation from the average number of published articles in a country that support a given narrative.",
		mapAudienceTooltipText: "The deviation from the average reaction to articles in a country that support a given narrative.",
		moeGraphAvailable: true,
		moeMapAvailable: true,
		moeTabName: "MOE",
		overrideColorsGently: true,
		projectSpecificTagFormat: formatGECTags,
		hideParentNarrativeTags: true,
		mediaLanguages: ["Latvian", "Russian", "Estonian", "Lithuanian", "Romanian"],
		// mediaPredictiveSelect: true,
		// mediaSearchHeaders: predictSearchHeaders,
		relevancySlider: false,
		showAllTopicsMap: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		statsPDF: true,
		timezone: false,
		translateArticleTitles: false,
		uiThemeToggle: true,
	},

	// This project uses the same API as GEC, the only difference is analystSystem: true
	gec_analyst: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			crosstabs: true,
			media: true, // need to set up api.dataKey
			narratives: true,
			bigMoversLink: false,
			curated: false,
			events: false,
			graph: false,
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		aiToggle: true,
		aiToggleNoReaction: true,
		alwaysShowContentInteractions: true,
		hideEntities: false,
		hideEntitiesInAIMode: true, // linked to aiMode
		analystSystem: true,
		compareHeaderConfig: false,
		compareMultiSelect: true,
		configureStats: configureGECStats,
		contentColumns: contentColumnsGECOnly,
		contentTabTableHeaders: contentTabBase,
		defaultAggregationLevel: "weeklyTimeseries",
		defaultOriginalLanguage: true,
		defaultEntityType: "People",
		defaultSearchTranslationEnabled: true,
		defaultSearchTranslationLanguages: ["ru"],
		desiredCrosstabsNarrativeOrder: ["Russian", "Non-Russian", "Kremlin-independent", "Kremlin-aligned", "Grey/Unknown"],
		duplicateToggle: false,
		emailSubscribeOption: "BAMO-alerts",
		entityFiltersAvailable: true, // <-----------
		crosstabsBargraphTopLeft: {
			id: "Market",
			name: "Target Market",
			// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
			limit: 15, // optional
			// tooltipText: biasText, // optional
			// tooltipBullets: ["string", "string"], // optional - supported, just not in use currently
		},
		crosstabsBargraphMiddleLeft: {
			id: "Bias",
			name: "Source Orientation",
			limit: 15,
			// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
			tooltipText: biasText,
		},
		crosstabsBargraphMiddleBottomLeft: {
			id: "Credibility",
			name: "Credibility",
			limit: 15,
		},
		crosstabsBargraphBottomLeft: {
			id: "Language",
			name: "Source Language",
			limit: 15,
		},
		crosstabsBargraphTopRight: {
			id: "Persons",
			name: "People",
			limit: 25,
			tooltipText: peopleText,
		},
		// FLAG_DATA_HANDLING_FACET
		// crosstabsNarrativeFacets: [{ name: "Outlet Alignment", id: "default" }, { name: "Country", id: "country" }],
		crosstabsNarrativeHeaders: gecCrosstabsHeaders,
		crosstabsNarrativeHeaderLabels: [
			{
				groupBy: "lang",
				label: "Language",
				info: "Outlet language",
			},
			{
				groupBy: "alignment",
				label: "Kremlin Orientation",
				info: "Outlet Kremlin alignment",
			},
			{
				groupBy: "country",
				label: "Country",
				info: "Articles published within a given country",
			},
		],
		fakeSingleArticle: false,
		graphDataTypes: [
			{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
			{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
			// { name: "Article Reach", id: "articleReach", label: "Reach" },
		],
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false,
		projectMapConfig: false,
		mapSourceTooltipText: "The deviation from the average number of published articles in a country that support a given narrative.",
		mapAudienceTooltipText: "The deviation from the average reaction to articles in a country that support a given narrative.",
		moeGraphAvailable: true,
		moeMapAvailable: true,
		moeTabName: "MOE",
		overrideColorsGently: true,
		projectSpecificTagFormat: formatGECTags,
		hideParentNarrativeTags: true,
		showAllTopicsMap: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		statsPDF: true,
		timezone: false,
		translateArticleTitles: false,
		uiThemeToggle: true,
	},

	afghanistandemo: {
		availableTabs: {
			default: "curated",
			asksage: false,
			curated: true,
			media: true, // need to set up api.dataKey
			reportBuilderLink: true,
			social: true,
			video: true, // set api.videoFilters
			bigMoversLink: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			moe: false,
			narratives: false,
			web: false,
		},
		compareHeaderConfig: false,
		contentTabCsvHeaders: [
			{
				name: "Title",
				id: "title",
			},
			{
				name: "Source",
				id: "source",
			},
			{
				name: "Date",
				id: "postDate",
			},
		],
		contentTabTableHeaders: contentTabSource,
		contentTabMap: false,
		defaultSearchTranslationEnabled: true,
		defaultSearchTranslationLanguages: ["fa"],
		defaultZoom: "1 year",
		entityFiltersAvailable: true, // <-----------
		fakeSingleArticle: false,
		hideEntities: false,
		hideParentNarrativeTags: true,
		hideShare: true,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false,
		mediaLanguages: ["English", "Pashto", "Dari"],
		userSearchHideLocation: true,
		projectMapConfig: false,
		// methodologyLink: "src/static/CBP_Methodology.docx",
		mediaEntityFilters: true,
		mediaTranslateTitles: false,
		moeGraphAvailable: true,
		moeMapAvailable: true,
		moeTabName: "MOE",
		overrideColors: true,
		percentageTypes: ["predata"],
		socialAccounts: [
			{ name: "Any", id: "all" },
			{ name: "All Tracked Accounts", id: "allTracked" },
			{ name: "Official Government", id: "aaaacccc-3333-4e71-a8fc-576eb20a8169" },
			// { name: "Taliban", id: "aaaacccc-3333-4d2b-a01f-12378ddafa70" },
			{ name: "Social Media Influencers", id: "aaaacccc-3333-4f53-98e1-764c6904cb59" },
			{ name: "Prominent News", id: "aaaacccc-3333-4959-8d74-db421e1fc7c7" },
		],
		socialPlatforms: [
			{ name: "Any", id: "all" },
			{ name: "Telegram", id: "telegram" },
			{ name: "Twitter", id: "twitter" },
		],
		socialLanguages: [
			// { name: "Any", id: "all" },
			{ name: "English", id: "english" },
			// { name: "Pashto", id: "pashto" },
			// { name: "Dari", id: "dari" },
		],
		socialLocation: [
			// { name: "All Locations", id: "all" },
			{ name: "Afghanistan", id: "afghanistan" },
		],
		socialMinDate: "2021-09-01", // "yyyy-mm-dd"
		socialMaxDate: "2021-10-01",
		videoTabEndDate: "10-01-2021", // "MM-DD-YYYY"
		videoTabStartDate: "09-01-2021", // "MM-DD-YYYY"
		contentTabDateBegin: 1630468800000,
		contentTabDateEnd: 1633060800000,
		socialTopics: [
			{ name: "Any", id: "all" },
			{ name: "All Topics", id: "allTracked" },
			{ name: "Security", id: "4267197c-3333-4d2b-a01f-12378ddafa70" },
			{ name: "Peace Process", id: "0fb387f0-3333-4959-8d74-db421e1fc7c7" },
			{ name: "Economics & Development", id: "37651f3e-3333-4e71-a8fc-576eb20a8169" },
			{ name: "Political & Governance", id: "54e1b4ae-3333-4f53-98e1-764c6904cb59" },
			// { name: "Afghanistan", id: "d303b515-3333-4052-9603-889c5f823f10" },
			// { name: "RS/Nato", id: "e3adf36a-3333-41ca-8d84-ddc7d6cdc2cd" },
			// { name: "Other Regional", id: "b1976529-3333-44d4-82fd-4e4fc704808c" },
		],
		// timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270, displayInSourceTimeZone: true },
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		translateArticleTitles: false,
		uiThemeToggle: true,
		videoTabHideTopics: true,
		videoTabEntities: true,
		videoTabEntityGraph: true,
		videoTabTopics: false,
		// videoTabStartDate: "01-01-2021", // "MM-DD-YYYY"
	},

	afghanistanDFAT: {
		availableTabs: {
			default: "curated",
			asksage: false,
			curated: true,
			media: true, // need to set up api.dataKey
			reportBuilderLink: true,
			social: false,
			video: true, // set api.videoFilters
			bigMoversLink: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			moe: false,
			narratives: false,
			web: false,
		},
		compareHeaderConfig: false,
		contentTabCsvHeaders: [
			{
				name: "Title",
				id: "title",
			},
			{
				name: "Source",
				id: "source",
			},
			{
				name: "Date",
				id: "postDate",
			},
		],
		contentTabTableHeaders: contentTabSource,
		contentTabMap: false,
		defaultSearchTranslationEnabled: true,
		defaultSearchTranslationLanguages: ["fa"],
		defaultZoom: "1 year",
		entityFiltersAvailable: true, // <-----------
		fakeSingleArticle: false,
		hideEntities: false,
		hideParentNarrativeTags: true,
		hideShare: true,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false,
		mediaLanguages: ["English", "Pashto", "Dari"],
		userSearchHideLocation: true,
		projectMapConfig: false,
		// methodologyLink: "src/static/CBP_Methodology.docx",
		mediaTranslateTitles: false,
		moeGraphAvailable: true,
		moeMapAvailable: true,
		moeTabName: "MOE",
		overrideColors: true,
		percentageTypes: ["predata"],
		socialAccounts: [
			{ name: "Any", id: "all" },
			// { name: "All Tracked Accounts", id: "allTracked" },
			{ name: "Official Government", id: "aaaacccc-3333-4e71-a8fc-576eb20a8169" },
			// { name: "Taliban", id: "aaaacccc-3333-4d2b-a01f-12378ddafa70" },
			{ name: "Social Media Influencers", id: "aaaacccc-3333-4f53-98e1-764c6904cb59" },
			{ name: "Prominent News", id: "aaaacccc-3333-4959-8d74-db421e1fc7c7" },
		],
		socialLanguages: [
			// { name: "Any", id: "all" },
			{ name: "English", id: "english" },
			// { name: "Pashto", id: "pashto" },
			// { name: "Dari", id: "dari" },
		],
		socialPlatforms: [
			{ name: "Any", id: "all" },
			{ name: "Telegram", id: "telegram" },
			{ name: "Twitter", id: "twitter" },
		],
		socialLocation: [
			// { name: "All Locations", id: "all" },
			{ name: "Afghanistan", id: "afghanistan" },
		],
		socialMinDate: "2020-12-17", // "yyyy-mm-dd"
		socialTopics: [
			{ name: "Any", id: "all" },
			// { name: "All Topics", id: "allTracked" },
			{ name: "Security", id: "4267197c-3333-4d2b-a01f-12378ddafa70" },
			{ name: "Peace Process", id: "0fb387f0-3333-4959-8d74-db421e1fc7c7" },
			{ name: "Economics & Development", id: "37651f3e-3333-4e71-a8fc-576eb20a8169" },
			{ name: "Political & Governance", id: "54e1b4ae-3333-4f53-98e1-764c6904cb59" },
			// { name: "Afghanistan", id: "d303b515-3333-4052-9603-889c5f823f10" },
			// { name: "RS/Nato", id: "e3adf36a-3333-41ca-8d84-ddc7d6cdc2cd" },
			// { name: "Other Regional", id: "b1976529-3333-44d4-82fd-4e4fc704808c" },
		],
		// timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270, displayInSourceTimeZone: true },
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		translateArticleTitles: false,
		uiThemeToggle: true,
		videoTabHideTopics: true,
		videoTabEntities: true,
		videoTabTopics: false,
		// videoTabStartDate: "01-01-2021", // "MM-DD-YYYY"
	},

	aatf: {
		availableTabs: {
			default: "curated",
			asksage: false,
			curated: true,
			media: false, // need to set up api.dataKey
			reportBuilderLink: false,
			social: false,
			video: false, // set api.videoFilters
			bigMoversLink: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			moe: false,
			narratives: false,
			web: false,
		},
		compareHeaderConfig: false,
		contentTabCsvHeaders: [
			{
				name: "Title",
				id: "title",
			},
			{
				name: "Source",
				id: "source",
			},
			{
				name: "Date",
				id: "postDate",
			},
		],
		contentTabTableHeaders: contentTabSource,
		contentTabMap: false,
		defaultSearchTranslationEnabled: true,
		defaultSearchTranslationLanguages: ["fa"],
		defaultZoom: "1 year",
		entityFiltersAvailable: false, // <-----------
		fakeSingleArticle: false,
		hideEntities: false,
		hideParentNarrativeTags: false,
		contentTabDateBegin: 1643707910000,
		hideShare: false,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false,
		mediaLanguages: ["English", "Pashto", "Dari"],
		userSearchHideLocation: true,
		projectMapConfig: false,
		// methodologyLink: "src/static/CBP_Methodology.docx",
		mediaTranslateTitles: false,
		moeGraphAvailable: true,
		moeMapAvailable: true,
		moeTabName: "MOE",
		overrideColors: true,
		percentageTypes: ["predata"],
		// videoTabStartDate: "01-01-2021", // "MM-DD-YYYY"
	},

	brasilia: {
		availableTabs: {
			default: "curated",
			asksage: false,
			curated: true,
			media: false, // need to set up api.dataKey
			reportBuilderLink: true,
			social: false,
			video: true, // set api.videoFilters
			bigMoversLink: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			moe: false,
			narratives: false,
			web: false,
		},
		compareHeaderConfig: false,
		contentTabCsvHeaders: [
			{
				name: "Title",
				id: "title",
			},
			{
				name: "Source",
				id: "source",
			},
			{
				name: "Date",
				id: "postDate",
			},
		],
		contentTabTableHeaders: contentTabSource,
		contentTabMap: false,
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		defaultZoom: "1 year",
		entityFiltersAvailable: true, // <-----------
		fakeSingleArticle: false,
		hideEntities: false,
		hideParentNarrativeTags: true,
		hideShare: true,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false,
		mediaLanguages: ["English", "Pashto", "Dari"],
		userSearchHideLocation: true,
		projectMapConfig: false,
		// methodologyLink: "src/static/CBP_Methodology.docx",
		mediaTranslateTitles: false,
		moeGraphAvailable: true,
		moeMapAvailable: true,
		moeTabName: "MOE",
		overrideColors: true,
		percentageTypes: ["predata"],
		socialAccounts: [
			{ name: "Any", id: "all" },
			{ name: "All Tracked Accounts", id: "allTracked" },
			{ name: "Official Government", id: "aaaacccc-3333-4e71-a8fc-576eb20a8169" },
			// { name: "Taliban", id: "aaaacccc-3333-4d2b-a01f-12378ddafa70" },
			{ name: "Social Media Influencers", id: "aaaacccc-3333-4f53-98e1-764c6904cb59" },
			{ name: "Prominent News", id: "aaaacccc-3333-4959-8d74-db421e1fc7c7" },
		],
		socialLanguages: [
			// { name: "Any", id: "all" },
			{ name: "English", id: "english" },
			// { name: "Pashto", id: "pashto" },
			// { name: "Dari", id: "dari" },
		],
		socialLocation: [
			// { name: "All Locations", id: "all" },
			{ name: "Afghanistan", id: "afghanistan" },
		],
		socialMinDate: "2020-12-17", // "yyyy-mm-dd"
		socialTopics: [
			{ name: "Any", id: "all" },
			{ name: "All Topics", id: "allTracked" },
			{ name: "Security", id: "4267197c-3333-4d2b-a01f-12378ddafa70" },
			{ name: "Peace Process", id: "0fb387f0-3333-4959-8d74-db421e1fc7c7" },
			{ name: "Economics & Development", id: "37651f3e-3333-4e71-a8fc-576eb20a8169" },
			{ name: "Political & Governance", id: "54e1b4ae-3333-4f53-98e1-764c6904cb59" },
			// { name: "Afghanistan", id: "d303b515-3333-4052-9603-889c5f823f10" },
			// { name: "RS/Nato", id: "e3adf36a-3333-41ca-8d84-ddc7d6cdc2cd" },
			// { name: "Other Regional", id: "b1976529-3333-44d4-82fd-4e4fc704808c" },
		],
		// timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270, displayInSourceTimeZone: true },
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		translateArticleTitles: false,
		uiThemeToggle: true,
		videoTabHideTopics: true,
		videoTabEntities: true,
		videoTabTopics: false,
		videoTabEndDate: "10-21-2021", // "MM-DD-YYYY"
		videoTabStartDate: "10-06-2021", // "MM-DD-YYYY"
		contentTabDateBegin: 1630468800000,
		contentTabDateEnd: 1634875200000,
		// videoTabStartDate: "01-01-2021", // "MM-DD-YYYY"
	},

	kabul: {
		availableTabs: {
			default: "curated",
			asksage: false,
			curated: true,
			media: true, // need to set up api.dataKey
			reportBuilderLink: true,
			social: false,
			video: true, // set api.videoFilters
			bigMoversLink: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			moe: false,
			narratives: false,
			web: false,
		},
		compareHeaderConfig: false,
		contentTabCsvHeaders: [
			{
				name: "Title",
				id: "title",
			},
			{
				name: "Source",
				id: "source",
			},
			{
				name: "Date",
				id: "postDate",
			},
		],
		contentTabTableHeaders: contentTabSource,
		contentTabMap: false,
		defaultSearchTranslationEnabled: true,
		defaultSearchTranslationLanguages: ["fa"],
		defaultZoom: "1 year",
		entityFiltersAvailable: true, // <-----------
		fakeSingleArticle: false,
		hideEntities: false,
		hideParentNarrativeTags: true,
		hideShare: true,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false,
		mediaLanguages: ["English", "Pashto", "Dari"],
		userSearchHideLocation: true,
		projectMapConfig: false,
		// methodologyLink: "src/static/CBP_Methodology.docx",
		mediaTranslateTitles: false,
		moeGraphAvailable: true,
		moeMapAvailable: true,
		moeTabName: "MOE",
		overrideColors: true,
		percentageTypes: ["predata"],
		socialAccounts: [
			{ name: "Any", id: "all" },
			// { name: "All Tracked Accounts", id: "allTracked" },
			{ name: "Official Government", id: "aaaacccc-3333-4e71-a8fc-576eb20a8169" },
			// { name: "Taliban", id: "aaaacccc-3333-4d2b-a01f-12378ddafa70" },
			{ name: "Social Media Influencers", id: "aaaacccc-3333-4f53-98e1-764c6904cb59" },
			{ name: "Prominent News", id: "aaaacccc-3333-4959-8d74-db421e1fc7c7" },
		],
		socialLanguages: [
			// { name: "Any", id: "all" },
			{ name: "English", id: "english" },
			// { name: "Pashto", id: "pashto" },
			// { name: "Dari", id: "dari" },
		],
		socialPlatforms: [
			{ name: "Any", id: "all" },
			{ name: "Telegram", id: "telegram" },
			{ name: "Twitter", id: "twitter" },
		],
		socialLocation: [
			// { name: "All Locations", id: "all" },
			{ name: "Afghanistan", id: "afghanistan" },
		],
		socialMinDate: "2020-12-17", // "yyyy-mm-dd"
		socialTopics: [
			{ name: "Any", id: "all" },
			// { name: "All Topics", id: "allTracked" },
			{ name: "Security", id: "4267197c-3333-4d2b-a01f-12378ddafa70" },
			{ name: "Peace Process", id: "0fb387f0-3333-4959-8d74-db421e1fc7c7" },
			{ name: "Economics & Development", id: "37651f3e-3333-4e71-a8fc-576eb20a8169" },
			{ name: "Political & Governance", id: "54e1b4ae-3333-4f53-98e1-764c6904cb59" },
			// { name: "Afghanistan", id: "d303b515-3333-4052-9603-889c5f823f10" },
			// { name: "RS/Nato", id: "e3adf36a-3333-41ca-8d84-ddc7d6cdc2cd" },
			// { name: "Other Regional", id: "b1976529-3333-44d4-82fd-4e4fc704808c" },
		],
		// timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270, displayInSourceTimeZone: true },
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		translateArticleTitles: false,
		uiThemeToggle: true,
		videoTabHideTopics: true,
		videoTabEntities: true,
		videoTabTopics: false,
		// videoTabStartDate: "01-01-2021", // "MM-DD-YYYY"
	},

	oseKabul: {
		availableTabs: {
			default: "curated",
			asksage: false,
			curated: true,
			media: true, // need to set up api.dataKey
			reportBuilderLink: true,
			social: false,
			video: true, // set api.videoFilters
			bigMoversLink: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			moe: false,
			narratives: false,
			web: false,
		},
		compareHeaderConfig: false,
		contentTabCsvHeaders: [
			{
				name: "Title",
				id: "title",
			},
			{
				name: "Source",
				id: "source",
			},
			{
				name: "Date",
				id: "postDate",
			},
		],
		contentTabTableHeaders: contentTabSource,
		contentTabMap: false,
		defaultSearchTranslationEnabled: true,
		defaultSearchTranslationLanguages: ["fa"],
		defaultZoom: "1 year",
		entityFiltersAvailable: true, // <-----------
		fakeSingleArticle: false,
		hideEntities: false,
		hideParentNarrativeTags: true,
		hideShare: true,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false,
		mediaLanguages: ["English", "Pashto", "Dari"],
		userSearchHideLocation: true,
		projectMapConfig: false,
		// methodologyLink: "src/static/CBP_Methodology.docx",
		mediaTranslateTitles: false,
		moeGraphAvailable: true,
		moeMapAvailable: true,
		moeTabName: "MOE",
		overrideColors: true,
		percentageTypes: ["predata"],
		socialAccounts: [
			{ name: "Any", id: "all" },
			// { name: "All Tracked Accounts", id: "allTracked" },
			{ name: "Official Government", id: "aaaacccc-3333-4e71-a8fc-576eb20a8169" },
			// { name: "Taliban", id: "aaaacccc-3333-4d2b-a01f-12378ddafa70" },
			{ name: "Social Media Influencers", id: "aaaacccc-3333-4f53-98e1-764c6904cb59" },
			{ name: "Prominent News", id: "aaaacccc-3333-4959-8d74-db421e1fc7c7" },
		],
		socialLanguages: [
			// { name: "Any", id: "all" },
			{ name: "English", id: "english" },
			// { name: "Pashto", id: "pashto" },
			// { name: "Dari", id: "dari" },
		],
		socialPlatforms: [
			{ name: "Any", id: "all" },
			{ name: "Telegram", id: "telegram" },
			{ name: "Twitter", id: "twitter" },
		],
		socialLocation: [
			// { name: "All Locations", id: "all" },
			{ name: "Afghanistan", id: "afghanistan" },
		],
		socialMinDate: "2020-12-17", // "yyyy-mm-dd"
		socialTopics: [
			{ name: "Any", id: "all" },
			// { name: "All Topics", id: "allTracked" },
			{ name: "Security", id: "4267197c-3333-4d2b-a01f-12378ddafa70" },
			{ name: "Peace Process", id: "0fb387f0-3333-4959-8d74-db421e1fc7c7" },
			{ name: "Economics & Development", id: "37651f3e-3333-4e71-a8fc-576eb20a8169" },
			{ name: "Political & Governance", id: "54e1b4ae-3333-4f53-98e1-764c6904cb59" },
			// { name: "Afghanistan", id: "d303b515-3333-4052-9603-889c5f823f10" },
			// { name: "RS/Nato", id: "e3adf36a-3333-41ca-8d84-ddc7d6cdc2cd" },
			// { name: "Other Regional", id: "b1976529-3333-44d4-82fd-4e4fc704808c" },
		],
		// timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270, displayInSourceTimeZone: true },
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		translateArticleTitles: false,
		uiThemeToggle: true,
		videoTabHideTopics: true,
		videoTabEntities: true,
		videoTabTopics: false,
		// videoTabStartDate: "01-01-2021", // "MM-DD-YYYY"
	},

	islamabad: {
		availableTabs: {
			default: "curated",
			asksage: false,
			curated: true,
			media: true, // need to set up api.dataKey
			reportBuilderLink: true,
			social: false,
			video: true, // set api.videoFilters
			bigMoversLink: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			moe: false,
			narratives: false,
			web: false,
		},
		compareHeaderConfig: false,
		contentTabCsvHeaders: [
			{
				name: "Title",
				id: "title",
			},
			{
				name: "Source",
				id: "source",
			},
			{
				name: "Date",
				id: "postDate",
			},
		],
		contentTabTableHeaders: contentTabSource,
		contentTabMap: false,
		defaultSearchTranslationEnabled: true,
		defaultSearchTranslationLanguages: ["ur"],
		defaultZoom: "1 month",
		entityFiltersAvailable: true, // <-----------
		fakeSingleArticle: false,
		hideEntities: false,
		hideParentNarrativeTags: true,
		hideShare: true,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false,
		mediaLanguages: ["English", "Urdu"],
		userSearchHideLocation: true,
		projectMapConfig: false,
		// methodologyLink: "src/static/CBP_Methodology.docx",
		mediaTranslateTitles: false,
		moeGraphAvailable: true,
		moeMapAvailable: true,
		moeTabName: "MOE",
		overrideColors: true,
		percentageTypes: ["predata"],
		socialAccounts: [
			{ name: "Any", id: "all" },
			{ name: "All Tracked Accounts", id: "allTracked" },
			{ name: "Official Government", id: "aaaacccc-3333-4e71-a8fc-576eb20a8169" },
			// { name: "Taliban", id: "aaaacccc-3333-4d2b-a01f-12378ddafa70" },
			{ name: "Social Media Influencers", id: "aaaacccc-3333-4f53-98e1-764c6904cb59" },
			{ name: "Prominent News", id: "aaaacccc-3333-4959-8d74-db421e1fc7c7" },
		],
		socialLanguages: [
			// { name: "Any", id: "all" },
			{ name: "English", id: "english" },
			{ name: "Pashto", id: "urdu" },
			// { name: "Dari", id: "dari" },
		],
		socialLocation: [
			// { name: "All Locations", id: "all" },
			{ name: "Pakistan", id: "pakistan" },
		],
		socialMinDate: "2021-06-01", // "yyyy-mm-dd"
		socialTopics: [
			{ name: "Any", id: "all" },
			{ name: "All Topics", id: "allTracked" },
			// { name: "Afghanistan", id: "d303b515-3333-4052-9603-889c5f823f10" },
			// { name: "RS/Nato", id: "e3adf36a-3333-41ca-8d84-ddc7d6cdc2cd" },
			// { name: "Other Regional", id: "b1976529-3333-44d4-82fd-4e4fc704808c" },
		],
		// timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270, displayInSourceTimeZone: true },
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		translateArticleTitles: false,
		uiThemeToggle: true,
		videoTabHideTopics: true,
		videoTabEntities: true,
		videoTabTopics: false,
		// videoTabStartDate: "01-01-2021", // "MM-DD-YYYY"
	},

	dcos: {
		availableTabs: {
			default: "curated",
			asksage: false,
			curated: true,
			media: true, // need to set up api.dataKey
			reportBuilderLink: true,
			social: true,
			video: true, // set api.videoFilters
			bigMoversLink: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			moe: false,
			narratives: false,
			web: false,
		},
		compareHeaderConfig: false,
		contentTabCsvHeaders: [
			{
				name: "Title",
				id: "title",
			},
			{
				name: "Source",
				id: "source",
			},
			{
				name: "Date",
				id: "postDate",
			},
		],
		contentTabTableHeaders: contentTabSource,
		contentTabMap: false,
		defaultSearchTranslationEnabled: true,
		defaultSearchTranslationLanguages: ["ur"],
		defaultZoom: "1 year",
		entityFiltersAvailable: true, // <-----------
		fakeSingleArticle: false,
		hideEntities: false,
		hideParentNarrativeTags: true,
		hideShare: true,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false,
		userSearchHideLocation: true,
		mediaLanguages: ["All", "English", "Pashto", "Dari"],
		googleTranslateLanguages: ["en", "ps", "fa"],
		mediaTranslateTitles: false,
		projectMapConfig: false,
		// methodologyLink: "src/static/CBP_Methodology.docx",
		moeGraphAvailable: true,
		moeMapAvailable: true,
		moeTabName: "MOE",
		overrideColors: true,
		percentageTypes: ["predata"],
		socialAccounts: [
			{ name: "Any", id: "all" },
			// { name: "All Tracked Accounts", id: "allTracked" },
			{ name: "Official Government", id: "aaaacccc-3333-4e71-a8fc-576eb20a8169" },
			{ name: "Taliban", id: "aaaacccc-3333-4d2b-a01f-12378ddafa70" },
			{ name: "Social Media Influencers", id: "aaaacccc-3333-4f53-98e1-764c6904cb59" },
			{ name: "Prominent News", id: "aaaacccc-3333-4959-8d74-db421e1fc7c7" },
		],

		socialLanguages: [
			{ name: "Any", id: "all" },
			{ name: "English", id: "English" },
			{ name: "Pashto", id: "Pushto" },
			{ name: "Dari/Farsi", id: "Persian" },
			{ name: "Arabic", id: "Arabic" },
		],
		socialLocation: [
			// { name: "All Locations", id: "all" },
			{ name: "Afghanistan", id: "afghanistan" },
		],
		socialPlatforms: [
			{ name: "Any", id: "all" },
			{ name: "Telegram", id: "telegram" },
			{ name: "Twitter", id: "twitter" },
		],
		socialMinDate: "2020-12-17", // "yyyy-mm-dd"
		socialTopics: [
			{ name: "Any", id: "all" },
			// { name: "All Topics", id: "allTracked" },
			{ name: "Security", id: "4267197c-3333-4d2b-a01f-12378ddafa70" },
			{ name: "Peace Process", id: "0fb387f0-3333-4959-8d74-db421e1fc7c7" },
			{ name: "Economics & Development", id: "37651f3e-3333-4e71-a8fc-576eb20a8169" },
			{ name: "Political & Governance", id: "54e1b4ae-3333-4f53-98e1-764c6904cb59" },
			{ name: "Afghanistan", id: "d303b515-3333-4052-9603-889c5f823f10" },
			{ name: "RS/Nato", id: "e3adf36a-3333-41ca-8d84-ddc7d6cdc2cd" },
			{ name: "Other Regional", id: "b1976529-3333-44d4-82fd-4e4fc704808c" },
		],
		// timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270, displayInSourceTimeZone: true },
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		translateArticleTitles: false,
		uiThemeToggle: true,
		videoTabEntities: true,
		videoTabStartDate: "12-01-2021", // "MM-DD-YYYY"
		videoTabTopics: false,
	},

	mf: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			narratives: true,
			crosstabs: true,
			bigMoversLink: false,
			curated: false,
			events: false,
			explorer: false,
			graph: false,
			media: false, // need to set up api.dataKey
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		// Default supports nested narratives with clickable main narrative
		compareHeaderConfig: false,
		contentColumns: noTypeNoDuplicatesContentColumns,
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		entityFiltersAvailable: true,
		entityLanguage: "original",
		fakeSingleArticle: false,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		hideEntities: false,
		hideMap: true,
		hideParentNarrativeTags: false,
		defaultLightUI: false,
		projectMapConfig: false,
		moeGraphAvailable: false,
		moeMapAvailable: false,
		overrideColors: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
		translateArticleTitles: false,
		uiThemeToggle: true,
	},

	mf_translate: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			media: true, // need to set up api.dataKey
			narratives: true,
			bigMoversLink: false,
			curated: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		// Default supports nested narratives with clickable main narrative
		compareHeaderConfig: false,
		contentColumns: noTypeNoDuplicatesContentColumns,
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		entityFiltersAvailable: true,
		entityLanguage: "original",
		fakeSingleArticle: false,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		hideEntities: false,
		hideMap: true,
		hideParentNarrativeTags: false,
		defaultLightUI: false,
		projectMapConfig: false,
		moeGraphAvailable: false,
		moeMapAvailable: false,
		overrideColors: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
		translateArticleTitles: false,
		uiThemeToggle: true,
	},

	nest: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			media: true, // need to set up api.dataKey
			narratives: true,
			bigMoversLink: false,
			curated: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		alwaysShowContentInteractions: true,
		// Default supports nested narratives with clickable main narrative
		compareHeaderConfig: false,
		contentColumns: noTypeNoDuplicatesContentColumns,
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		entityFiltersAvailable: true,
		entityLanguage: "original",
		fakeSingleArticle: false,
		graphDataTypes: [
			{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
			{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
			// { name: "Article Reach", id: "articleReach", label: "Reach" },
		],
		hideArticlesExport: true,
		hideEntities: false,
		hideMap: true,
		hideParentNarrativeTags: false,
		hideShare: false,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false,
		userSearchHideLocation: true,
		projectMapConfig: false,
		moeGraphAvailable: false,
		moeMapAvailable: false,
		noReaction: true,
		overrideColors: true,
		showAllTopicsMap: false,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
		topicsPie: true,
		topicStatsConfig: chinaUsStats,
		translateArticleTitles: false,
		uiThemeToggle: true,
	},

	nest_mongolia: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			media: true, // need to set up api.dataKey
			narratives: true,
			bigMoversLink: false,
			curated: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		alwaysShowContentInteractions: true,
		// Default supports nested narratives with clickable main narrative
		compareHeaderConfig: false,
		contentColumns: noTypeNoDuplicatesContentColumns,
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		entityFiltersAvailable: true,
		entityLanguage: "original",
		fakeSingleArticle: false,
		graphDataTypes: [
			{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
			{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
			// { name: "Article Reach", id: "articleReach", label: "Reach" },
		],
		hideArticlesExport: true,
		hideEntities: false,
		hideMap: true,
		hideParentNarrativeTags: false,
		hideShare: false,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false,
		projectMapConfig: false,
		moeGraphAvailable: false,
		moeMapAvailable: false,
		noReaction: true,
		overrideColors: true,
		showAllTopicsMap: false,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
		topicsPie: true,
		topicsPieArray: true,
		topicStatsConfig: chinaUsStatsMongolia,
		translateArticleTitles: false,
		uiThemeToggle: true,
	},

	politics2020: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			crosstabs: true,
			narratives: true,
			bigMoversLink: false,
			curated: false,
			events: false,
			explorer: false,
			graph: false,
			media: false,
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		alwaysShowContentInteractions: true,
		// Default supports nested narratives with clickable main narrative
		compareHeaderConfig: false,
		contentColumns: noTypeContentColumns,
		defaultAggregationLevel: "weeklyTimeseries",
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		desiredCrosstabsNarrativeOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"],
		dmaMap: true,
		entityFiltersAvailable: true,
		crosstabsBargraphTopLeft: {
			id: "Bias",
			name: "Political Bias",
			desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
			// limit: Number,  // optional
			tooltipText: biasText, // optional
			// tooltipBullets: ["string", "string"], // optional - supported, just not in use currently
			// disableMax: true, // FLAG_DISABLE_MAX - unsupported for now. will disable left side chartMax if any pass this (implementation sounds clunky - may want to rethink it as an additional config item)
		},
		crosstabsBargraphMiddleLeft: {
			id: "Outlets",
			name: "Outlets",
			limit: 18,
		},
		// crosstabsBargraphBottomLeft:
		crosstabsBargraphTopRight: {
			id: "Persons",
			name: "People",
			limit: 25,
			tooltipText: peopleText,
		},
		// crosstabsBargraphMiddleRight:
		// crosstabsBargraphBottomRight:
		// FLAG_DATA_HANDLING_FACET
		crosstabsNarrativeFacets: [
			{ name: "Political Bias", id: "default" },
			{ name: "State Leaning", id: "stateLeaning" },
		],
		crosstabsNarrativeHeaders: us2020CrosstabsHeaders,
		crosstabsNarrativeHeaderLabels: [
			{
				groupBy: "bias",
				label: "Political Bias",
				info: "Articles published by outlets, grouped by political bias",
			},
			{
				groupBy: "stateLeaning",
				label: "State Political Leaning",
				info: "Articles published in states, grouped by their projected political alignment in the 2020 election",
				bullets: [
					"Battleground (13): CO, FL, IA, ME, MI, MN, NC, NH, NV, OH, PA, VA, WI",
					"Left (19): CA, CT, DC, DE, HI, IL, MA, MD, NM, NJ, NY, OR, RI, VT, WA",
					"Right (21): AK, AL, AR, AZ, GA, ID, IN, KS, KY, LA, MO, OK, MS, MT, NE, ND, SC, SD, TN, TX, UT, WV, WY",
				],
			},
		],
		exoveraEditButton: true,
		expandedContentShowTweets: false,
		fakeSingleArticle: false,
		graphDataTypes: [
			{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
			{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
			// { name: "Article Reach", id: "articleReach", label: "Reach" },
		],
		hotArticleLimit: {
			hot: 1000,
			hotter: 5000,
			hottest: 10000,
		},
		hideEntities: false,
		hideParentNarrativeTags: false,
		highlightedEntitiesTitle: "Candidates",
		defaultLightUI: false,
		projectMapConfig: false,
		mapLookup: usFIPSLookup,
		moeGraphAvailable: false,
		moeMapAvailable: false,
		overrideColors: true,
		showAllTopicsMap: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
		topPhrasesAvailable: true,
		uiThemeToggle: true,
	},

	politics2020live: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			crosstabs: true,
			narratives: true,
			bigMoversLink: false,
			curated: false,
			events: false,
			explorer: false,
			graph: false,
			media: false,
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		alwaysShowContentInteractions: true,
		// Default supports nested narratives with clickable main narrative
		compareHeaderConfig: false,
		contentColumns: noTypeContentColumns,
		defaultAggregationLevel: "weeklyTimeseries",
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		desiredCrosstabsNarrativeOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"],
		dmaMap: true,
		entityFiltersAvailable: true,
		crosstabsBargraphTopLeft: {
			id: "Bias",
			name: "Political Bias",
			desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
			// limit: Number,  // optional
			tooltipText: biasText, // optional
			// tooltipBullets: ["string", "string"], // optional - supported, just not in use currently
			// disableMax: true, // FLAG_DISABLE_MAX - unsupported for now. will disable left side chartMax if any pass this (implementation sounds clunky - may want to rethink it as an additional config item)
		},
		crosstabsBargraphMiddleLeft: {
			id: "Outlets",
			name: "Outlets",
			limit: 18,
		},
		// crosstabsBargraphBottomLeft:
		crosstabsBargraphTopRight: {
			id: "Persons",
			name: "People",
			limit: 25,
			tooltipText: peopleText,
		},
		// crosstabsBargraphMiddleRight:
		// crosstabsBargraphBottomRight:
		// FLAG_DATA_HANDLING_FACET
		crosstabsNarrativeFacets: [
			{ name: "Political Bias", id: "default" },
			{ name: "State Leaning", id: "stateLeaning" },
		],
		crosstabsNarrativeHeaders: us2020CrosstabsHeaders,
		crosstabsNarrativeHeaderLabels: [
			{
				groupBy: "bias",
				label: "Political Bias",
				info: "Articles published by outlets, grouped by political bias",
			},
			{
				groupBy: "stateLeaning",
				label: "State Political Leaning",
				info: "Articles published in states, grouped by their projected political alignment in the 2020 election",
				bullets: [
					"Battleground (13): CO, FL, IA, ME, MI, MN, NC, NH, NV, OH, PA, VA, WI",
					"Left (19): CA, CT, DC, DE, HI, IL, MA, MD, NM, NJ, NY, OR, RI, VT, WA",
					"Right (21): AK, AL, AR, AZ, GA, ID, IN, KS, KY, LA, MO, OK, MS, MT, NE, ND, SC, SD, TN, TX, UT, WV, WY",
				],
			},
		],
		exoveraEditButton: true,
		expandedContentShowTweets: true,
		fakeSingleArticle: false,
		graphDataTypes: [
			{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
			{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
			// { name: "Article Reach", id: "articleReach", label: "Reach" },
		],
		hotArticleLimit: {
			hot: 1000,
			hotter: 5000,
			hottest: 10000,
		},
		hideEntities: false,
		hideParentNarrativeTags: false,
		highlightedEntitiesTitle: "Candidates",
		defaultLightUI: false,
		projectMapConfig: false,
		mapLookup: usFIPSLookup,
		moeGraphAvailable: false,
		moeMapAvailable: false,
		overrideColors: true,
		showAllTopicsMap: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
		topPhrasesAvailable: true,
		uiThemeToggle: true,
	},

	russia: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			moe: true,
			narratives: true,
			bigMoversLink: false,
			curated: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			media: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		aiToggle: false,
		hideMap: true,

		// aiToggleNoReaction: true,
		alwaysShowContentInteractions: true,
		// hideEntitiesInAIMode: true, // Linked to aiMode
		compareHeaderConfig: false,
		compareMultiSelect: true,
		// configureStats: configureGECStats, // for crosstabs
		contentColumns: contentColumnsGECOnly,
		// TODO: Install this and refactor summary-tab to consume it if the data ever support it
		// contentTabLabelPositions: {
		// 	topLeft: false,
		// 	topRight: "postDate",
		// 	bottomLeft: "featured",
		// 	bottomRight: false,
		// },
		// contentTabTableHeaders: contentTabBase,
		// defaultAggregationLevel: "weeklyTimeseries",
		defaultOriginalLanguage: true,
		defaultEntityType: "People",
		defaultSearchTranslationEnabled: true,
		defaultSearchTranslationLanguages: ["ru"],
		// desiredCrosstabsNarrativeOrder: ["Russian", "Non-Russian", "Kremlin-independent", "Kremlin-aligned", "Grey/Unknown"],
		// emailSubscribeOption: "BAMO-alerts",
		entityFiltersAvailable: true, // <-----------
		expandedContentShowTweets: false,
		// crosstabsBargraphTopLeft: {
		// 	id: "Market",
		// 	name: "Target Market",
		// 	// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
		// 	limit: 15, // optional
		// 	// tooltipText: biasText, // optional
		// 	// tooltipBullets: ["string", "string"], // optional - supported, just not in use currently
		// },
		// crosstabsBargraphMiddleLeft: {
		// 	id: "Bias",
		// 	name: "Source Orientation",
		// 	limit: 15,
		// 	// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
		// 	tooltipText: biasText,
		// },
		// crosstabsBargraphMiddleBottomLeft: {
		// 	id: "Credibility",
		// 	name: "Credibility",
		// 	limit: 15,
		// },
		// crosstabsBargraphBottomLeft: {
		// 	id: "Language",
		// 	name: "Source Language",
		// 	limit: 15,
		// },
		// crosstabsBargraphTopRight: {
		// 	id: "Persons",
		// 	name: "People",
		// 	limit: 25,
		// 	tooltipText: peopleText,
		// },
		// FLAG_DATA_HANDLING_FACET
		// crosstabsNarrativeFacets: [{ name: "Outlet Alignment", id: "default" }, { name: "Country", id: "country" }],
		// crosstabsNarrativeHeaders: gecCrosstabsHeaders,
		// crosstabsNarrativeHeaderLabels: [
		// 	{
		// 		groupBy: "lang",
		// 		label: "Language",
		// 		info: "Outlet language",
		// 	},
		// 	{
		// 		groupBy: "alignment",
		// 		label: "Kremlin Orientation",
		// 		info: "Outlet Kremlin alignment",
		// 	},
		// 	{
		// 		groupBy: "country",
		// 		label: "Country",
		// 		info: "Articles published within a given country",
		// 	},
		// ],
		duplicateToggle: false,
		fakeSingleArticle: false,
		graphDataTypes: [
			{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
			{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
			// { name: "Article Reach", id: "articleReach", label: "Reach" },
		],
		hideEntities: false,
		hideParentNarrativeTags: true,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false, // default to defaultLightUI?
		projectMapConfig: false,
		mapSourceTooltipText: "The deviation from the average number of published articles in a country that support a given narrative.",
		mapAudienceTooltipText: "The deviation from the average reaction to articles in a country that support a given narrative.",
		moeGraphAvailable: true,
		moeMapAvailable: false,
		moeTabName: "MOE",
		overrideColorsGently: true,
		projectSpecificTagFormat: formatGECTags,
		// mediaLanguages: ["Latvian", "Russian", "Estonian", "Lithuanian", "Romanian"],
		// mediaPredictiveSelect: true,
		// mediaSearchHeaders: predictSearchHeaders,
		relevancySlider: false,
		showAllTopicsMap: false,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		statsPDF: true,
		timezone: false,
		translateArticleTitles: false,
		uiThemeToggle: true,
	},

	bsca: {
		availableTabs: {
			default: "curated",
			asksage: false,
			curated: true,
			media: true, // need to set up api.dataKey
			reportBuilderLink: true,
			video: true, // set api.videoFilters
			bigMoversLink: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			moe: false,
			narratives: false,
			social: false,
			web: false,
		},
		contentTabCsvHeaders: [
			{
				name: "Title",
				id: "title",
			},
			{
				name: "Source",
				id: "source",
			},
			{
				name: "Date",
				id: "postDate",
			},
		],
		contentTabTableHeaders: contentTabSource,
		contentTabMap: false,
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		defaultZoom: "1 year",
		expandedContentShowTweets: false,
		hideEntities: false,
		hideShare: true,
		defaultLightUI: false,
		userSearchHideLocation: true,
		mediaLanguages: ["English"],
		// timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270, displayInSourceTimeZone: true },
		uiThemeToggle: true,
		videoTabEntities: true,
		videoTabTopics: false,
	},

	sevenTwenty: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			narratives: true,
			bigMoversLink: false,
			curated: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			media: false,
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		compareHeaderConfig: [
			{ id: "narratives", header: "Narratives" },
			{ id: "additionalSearches", header: "Other Trends" },
		],
		contentColumns: noTypeContentColumns,
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		duplicateToggle: true,
		entityFiltersAvailable: true,
		expandedContentShowTweets: true,
		fakeSingleArticle: false,
		hideEntities: false,
		hideMapIndicatorInGraph: true,
		hotArticleLimit: {
			hot: 1000,
			hotter: 2000,
			hottest: 5000,
		},
		defaultLightUI: true,
		projectMapConfig: false,
		moeGraphAvailable: false, // <-----------
		moeMapAvailable: true,
		overrideColors: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
	},

	sevenTwenty_uscc: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			narratives: true,
			bigMoversLink: false,
			curated: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			media: false,
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		// Default supports nested narratives with clickable main narrative
		compareHeaderConfig: [
			{ id: "narratives", header: "Narratives" },
			{ id: "additionalSearches", header: "Other Trends" },
		],
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		entityFiltersAvailable: true,
		fakeSingleArticle: false,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		hideEntities: false,
		hideParentNarrativeTags: false,
		defaultLightUI: true,
		projectMapConfig: {
			narratives: {
				state: { name: "Texas 23rd", geoState: "texas23" },
			},
			compare: {
				state: { name: "Texas 23rd", geoState: "texas23" },
			},
			moe: {
				state: { name: "Texas 23rd", geoState: "texas23" },
			},
		},
		moeGraphAvailable: true,
		moeMapAvailable: true,
		overrideColors: true,
		showTopicsInGraph: false, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
	},

	usip: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			narratives: true,
			bigMoversLink: false,
			curated: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			media: false,
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		compareHeaderConfig: false,
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		entityFiltersAvailable: true, // <-----------
		fakeSingleArticle: false,
		hideEntities: false,
		hideParentNarrativeTags: true,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false,
		projectMapConfig: false,
		moeGraphAvailable: true,
		moeMapAvailable: true,
		moeTabName: "MOE",
		overrideColors: true,
		showAllTopicsMap: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270, displayInSourceTimeZone: true },
		uiThemeToggle: true,
	},

	venezuela: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			narratives: true,
			bigMoversLink: false,
			curated: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			media: false,
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		compareHeaderConfig: [{ id: "narratives", header: "Narratives" }],
		contentColumns: vzlaContentColumns,
		contentHeaderSortAudienceDefault: "fdate", // non-config default is "shares"
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		entityFiltersAvailable: false, // <-----------
		expandedContentShowTweets: true,
		fakeSingleArticle: false,
		hideEntities: false,
		hideParentNarrativeTags: false,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false,
		projectMapConfig: false,
		moeGraphAvailable: true,
		moeMapAvailable: true,
		moeTabName: "MOE",
		overrideColors: true,
		showTopicsInGraph: false, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
	},

	// venezuela clone
	sayara: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			narratives: true,
			bigMoversLink: false,
			curated: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			media: false,
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		compareHeaderConfig: [{ id: "narratives", header: "Narratives" }],
		contentColumns: vzlaContentColumns,
		contentHeaderSortAudienceDefault: "fdate", // non-config default is "shares"
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		entityFiltersAvailable: false, // <-----------
		expandedContentShowTweets: true,
		fakeSingleArticle: false,
		hideEntities: false,
		hideParentNarrativeTags: false,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		defaultLightUI: false,
		projectMapConfig: false,
		moeGraphAvailable: true,
		moeMapAvailable: true,
		moeTabName: "MOE",
		overrideColors: true,
		showTopicsInGraph: false, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
	},

	uspol: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			narratives: true,
			bigMoversLink: false,
			curated: false,
			crosstabs: false,
			events: false,
			explorer: false,
			graph: false,
			media: false,
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		// Default supports nested narratives with clickable main narrative
		compareHeaderConfig: false,
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		entityFiltersAvailable: true,
		fakeSingleArticle: false,
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		hideEntities: false,
		hideMap: true,
		hideParentNarrativeTags: false,
		defaultLightUI: false,
		projectMapConfig: false,
		moeGraphAvailable: false,
		moeMapAvailable: false,
		overrideColors: true,
		showAllTopicsMap: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
		uiThemeToggle: true,
	},

	walmart: {
		availableTabs: {
			default: "narratives",
			asksage: false,
			crosstabs: true,
			narratives: true,
			bigMoversLink: false,
			curated: false,
			events: false,
			explorer: false,
			graph: false,
			media: false,
			moe: false,
			reportBuilderLink: false,
			social: false,
			video: false,
			web: false,
		},
		altProjectForCrosstabsNarratives: "politics2020c4c66e01afca57f4896cd084f0c73df0",
		// Default supports nested narratives with clickable main narrative
		compareHeaderConfig: false,
		contentColumns: dateTypeTitleSharesContentColumns,
		defaultSearchTranslationEnabled: false,
		defaultSearchTranslationLanguages: [],
		entityFiltersAvailable: true,
		crosstabsBargraphTopLeft: {
			id: "Bias",
			name: "Political Bias",
			desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"],
			tooltipText: biasText,
		},
		crosstabsBargraphMiddleLeft: {
			id: "Outlets",
			name: "Outlets",
			limit: 18,
		},
		crosstabsBargraphTopRight: {
			id: "Persons",
			name: "People",
			limit: 25,
			tooltipText: peopleText,
		},
		// expandedContentShowTweets: true,
		fakeSingleArticle: false,
		forceCrosstabsEntity: "Walmart",
		hotArticleLimit: {
			hot: 50,
			hotter: 100,
			hottest: 500,
		},
		hideEntities: false,
		hideParentNarrativeTags: false,
		defaultLightUI: false,
		projectMapConfig: false,
		moeGraphAvailable: false,
		moeMapAvailable: false,
		overrideColors: true,
		showAllTopicsMap: true,
		showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
		timezone: false,
		uiThemeToggle: false,
	},
	
	asksage: {
		availableTabs: {
			default: "asksage",
			asksage: true,
		},
		entityFiltersAvailable: false,
		defaultLightUI: false,
		timezone: false,
		uiThemeToggle: true,
	},
};

export default projectConfig;
